import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { CalendarCheck, PlaneTakeoff } from "lucide-react";
import SinoPessoal from "../components/student/SinoPessoal";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  Plane, LogOut, Users, BookOpen, FileText, Plus, 
  GraduationCap, TrendingUp, AlertTriangle, ChevronRight, ChevronLeft,
  Eye, Download, XCircle, Trash2, Unlock, Clock, CheckCircle,
  PlayCircle, Lock, FileDown, MoreVertical, Shield, LayoutDashboard,
  Rocket, Award, Target, Edit2
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import AnacTheoryDialog from "../components/anac/AnacTheoryDialog";
import { ANAC_STATUS_CLASS, ANAC_STATUS_LABEL, nextExamClass, fmtDayIso } from "../components/anac/anacStatus";
import AnacPanorama from "../components/anac/AnacPanorama";
import { BookOpen as HelpBook } from "lucide-react";
import { helpPath } from "../help";

// Helper to format percentage with 2 decimal places
const formatPercent = (value) => {
  if (value === null || value === undefined) return '0.00';
  return Number(value).toFixed(2);
};

// Mini sparkline for exam score trend
const ExamSparkline = ({ scores, width = 60, height = 16 }) => {
  if (!scores || scores.length < 2) return null;
  const pad = 2;
  const w = width - pad * 2;
  const h = height - pad * 2;
  const maxV = 100;
  const minV = 0;
  const range = maxV - minV;
  const step = scores.length > 1 ? w / (scores.length - 1) : 0;
  const points = scores.map((s, i) => {
    const x = pad + i * step;
    const y = pad + h - ((s - minV) / range) * h;
    return [x, y];
  });
  const polyline = points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  // Determine trend color from first->last
  const trend = scores[scores.length - 1] - scores[0];
  const strokeColor = trend > 2 ? '#34d399' : trend < -2 ? '#f87171' : '#94a3b8';
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="inline-block align-middle ml-1"
      data-testid="exam-sparkline"
    >
      {/* 75% threshold line */}
      <line
        x1={pad}
        x2={width - pad}
        y1={pad + h - (75 / range) * h}
        y2={pad + h - (75 / range) * h}
        stroke="#475569"
        strokeWidth="0.5"
        strokeDasharray="2 2"
      />
      <polyline
        points={polyline}
        fill="none"
        stroke={strokeColor}
        strokeWidth="1.2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {points.map(([x, y], i) => (
        <circle
          key={`${scores[i]}-${i}`}
          cx={x}
          cy={y}
          r="1.3"
          fill={scores[i] >= 75 ? '#34d399' : '#f87171'}
        />
      ))}
    </svg>
  );
};

const InstructorDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [classes, setClasses] = useState([]);
  const [exams, setExams] = useState([]);
  const [sections, setSections] = useState([]);
  const [cadernos, setCadernos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  // Stats filters
  const [statsClassFilter, setStatsClassFilter] = useState("all");
  const [statsSectionFilter, setStatsSectionFilter] = useState("all");
  const [filteredStats, setFilteredStats] = useState(null);
  
  // Errors pagination
  const [errorsPage, setErrorsPage] = useState(1);
  const [errorsPagination, setErrorsPagination] = useState(null);
  const [loadingErrors, setLoadingErrors] = useState(false);

  // Exams filter
  const [examsClassFilter, setExamsClassFilter] = useState("all");
  // Hide both regular and preparation exam lists by default — instructors
  // landing on this tab should NOT see noise unless they explicitly opt in.
  const [showRegularExams, setShowRegularExams] = useState(false);
  const [showPreparationExams, setShowPreparationExams] = useState(false);

  // Students stats
  const [studentsStats, setStudentsStats] = useState([]);
  const [studentsClassFilter, setStudentsClassFilter] = useState("all");
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [expandedStudent, setExpandedStudent] = useState(null);

  // New class form
  const [newClassName, setNewClassName] = useState("");
  const [newClassDesc, setNewClassDesc] = useState("");
  const [newClassStart, setNewClassStart] = useState("");
  const [showNewClassDialog, setShowNewClassDialog] = useState(false);

  // New exam form
  const [newExamTitle, setNewExamTitle] = useState("");
  const [newExamDuration, setNewExamDuration] = useState(60);
  const [newExamQuestions, setNewExamQuestions] = useState(40);
  const [newExamSections, setNewExamSections] = useState([]);
  const [newExamCadernos, setNewExamCadernos] = useState([]);
  const [newExamClass, setNewExamClass] = useState("");
  const [newExamDifficulty, setNewExamDifficulty] = useState(""); // "", "easy", "medium", "hard"
  const [newExamCountsForStats, setNewExamCountsForStats] = useState(true);
  const [showNewExamDialog, setShowNewExamDialog] = useState(false);
  
  // Student filter for new exam
  const [newExamStudentFilter, setNewExamStudentFilter] = useState("all"); // "all", "select", "exclude"
  const [newExamSelectedStudents, setNewExamSelectedStudents] = useState([]); // IDs selected or excluded
  const [classStudents, setClassStudents] = useState([]); // Students in selected class
  const [loadingClassStudents, setLoadingClassStudents] = useState(false);

  // Edit student name state
  const [showEditStudentNameDialog, setShowEditStudentNameDialog] = useState(false);
  const [editingStudentId, setEditingStudentId] = useState(null);
  const [editingStudentName, setEditingStudentName] = useState("");
  const [savingStudentName, setSavingStudentName] = useState(false);
  // Teóricas ANAC do aluno escolhido na lista da turma
  const [anacStudent, setAnacStudent] = useState(null);

  // Handle student name edit
  const handleEditStudentName = (studentId, currentName) => {
    setEditingStudentId(studentId);
    setEditingStudentName(currentName || "");
    setShowEditStudentNameDialog(true);
  };

  const handleSaveStudentName = async () => {
    if (!editingStudentName.trim() || editingStudentName.trim().length < 2) {
      toast.error("O nome deve ter pelo menos 2 caracteres");
      return;
    }
    
    setSavingStudentName(true);
    try {
      await axios.put(
        `${API}/instructor/students/${editingStudentId}/name`, 
        { name: editingStudentName.trim() }, 
        { withCredentials: true }
      );
      toast.success("Nome do aluno atualizado com sucesso");
      setShowEditStudentNameDialog(false);
      // Refresh data
      fetchStudentsStats();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar nome");
    } finally {
      setSavingStudentName(false);
    }
  };

  // Fetch students when class changes
  const fetchClassStudents = async (classId) => {
    if (!classId) {
      setClassStudents([]);
      return;
    }
    
    setLoadingClassStudents(true);
    try {
      const response = await axios.get(
        `${API}/instructor/classes/${classId}/students`,
        { withCredentials: true }
      );
      setClassStudents(response.data || []);
    } catch (error) {
      console.error("Error fetching class students:", error);
      setClassStudents([]);
    } finally {
      setLoadingClassStudents(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === "students") {
      fetchStudentsStats();
    }
  }, [activeTab, studentsClassFilter]);

  const fetchStudentsStats = async () => {
    setLoadingStudents(true);
    try {
      const params = studentsClassFilter !== "all" ? `?class_id=${studentsClassFilter}` : "";
      const res = await axios.get(`${API}/instructor/students-stats${params}`, { withCredentials: true });
      setStudentsStats(res.data);
    } catch (error) {
      console.error("Error fetching students stats:", error);
      toast.error("Erro ao carregar estatísticas dos alunos");
    } finally {
      setLoadingStudents(false);
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, classesRes, examsRes, sectionsRes, cadernosRes] = await Promise.all([
        axios.get(`${API}/instructor/stats`, { withCredentials: true }),
        axios.get(`${API}/instructor/classes`, { withCredentials: true }),
        axios.get(`${API}/instructor/exams`, { withCredentials: true }),
        axios.get(`${API}/sections`, { withCredentials: true }),
        axios.get(`${API}/cadernos`, { withCredentials: true })
      ]);
      
      setStats(statsRes.data);
      setClasses(classesRes.data);
      setExams(examsRes.data);
      setSections(sectionsRes.data);
      setCadernos(cadernosRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const classesRes = await axios.get(`${API}/instructor/classes`, { withCredentials: true });
      setClasses(classesRes.data);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  const fetchFilteredStats = async (page = errorsPage) => {
    setLoadingErrors(true);
    try {
      const params = new URLSearchParams();
      if (statsClassFilter !== "all") params.append("class_id", statsClassFilter);
      if (statsSectionFilter !== "all") params.append("section_id", statsSectionFilter);
      params.append("page", page.toString());
      params.append("limit", "10");
      
      const response = await axios.get(
        `${API}/instructor/stats?${params.toString()}`,
        { withCredentials: true }
      );
      setFilteredStats(response.data);
      setErrorsPagination(response.data.pagination);
    } catch (error) {
      console.error("Error fetching filtered stats:", error);
    } finally {
      setLoadingErrors(false);
    }
  };

  // Update filtered stats when filters change
  useEffect(() => {
    if (stats) {
      setErrorsPage(1); // Reset to page 1 when filters change
      fetchFilteredStats(1);
    }
  }, [statsClassFilter, statsSectionFilter, stats]);

  // Fetch when page changes
  useEffect(() => {
    if (stats && errorsPage > 0) {
      fetchFilteredStats(errorsPage);
    }
  }, [errorsPage]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const createClass = async () => {
    if (!newClassName.trim()) {
      toast.error("Nome da turma é obrigatório");
      return;
    }
    
    try {
      await axios.post(`${API}/instructor/classes`, {
        name: newClassName,
        description: newClassDesc,
        course_start_date: newClassStart || null
      }, { withCredentials: true });

      toast.success("Turma criada com sucesso");
      setNewClassName("");
      setNewClassDesc("");
      setNewClassStart("");
      setShowNewClassDialog(false);
      fetchData();
    } catch (error) {
      toast.error("Erro ao criar turma");
    }
  };

  const createExam = async () => {
    if (!newExamTitle.trim()) {
      toast.error("Título do exame é obrigatório");
      return;
    }
    
    // Calculate assigned students based on filter
    let assignedStudents = [];
    
    if (newExamClass && newExamStudentFilter !== "all") {
      if (newExamStudentFilter === "select") {
        // Only selected students
        assignedStudents = newExamSelectedStudents;
      } else if (newExamStudentFilter === "exclude") {
        // All except excluded students
        assignedStudents = classStudents
          .filter(s => !newExamSelectedStudents.includes(s.user_id))
          .map(s => s.user_id);
      }
    }
    // If "all" or no class, let backend handle the assignment
    
    try {
      await axios.post(`${API}/instructor/exams`, {
        title: newExamTitle,
        duration_minutes: newExamDuration,
        num_questions: newExamQuestions,
        sections: newExamSections,
        cadernos: newExamCadernos,
        class_id: newExamClass || null,
        assigned_students: assignedStudents,
        difficulty_level: newExamDifficulty || null,
        counts_for_stats: newExamCountsForStats
      }, { withCredentials: true });
      
      const studentInfo = newExamStudentFilter === "all" || !newExamClass
        ? ""
        : newExamStudentFilter === "select" 
          ? ` para ${assignedStudents.length} aluno(s)`
          : ` (${classStudents.length - newExamSelectedStudents.length} alunos)`;
      
      toast.success(`Exame criado com sucesso${studentInfo} (aguarda início)`);
      setNewExamTitle("");
      setNewExamDuration(60);
      setNewExamQuestions(40);
      setNewExamSections([]);
      setNewExamCadernos([]);
      setNewExamClass("");
      setNewExamDifficulty("");
      setNewExamCountsForStats(true);
      setNewExamStudentFilter("all");
      setNewExamSelectedStudents([]);
      setClassStudents([]);
      setShowNewExamDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar exame");
    }
  };

  const startExam = async (examId) => {
    if (!confirm("Iniciar o exame? Os alunos terão acesso imediato e o tempo começará a contar.")) {
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/instructor/exams/${examId}/start`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.message || "Exame iniciado!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao iniciar exame");
    }
  };

  const stopExam = async (examId) => {
    if (!confirm("Terminar o exame? Os alunos não poderão mais submeter respostas.")) {
      return;
    }
    
    try {
      await axios.post(
        `${API}/instructor/exams/${examId}/stop`,
        {},
        { withCredentials: true }
      );
      toast.success("Exame terminado");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao terminar exame");
    }
  };

  // Filter exams by class
  // Logic:
  // - Include exams explicitly assigned to the selected class
  // - Include "all classes" exams ONLY if the class existed when the exam was created
  const filteredExams = examsClassFilter === "all" 
    ? exams 
    : exams.filter(e => {
        // Get the selected class info
        const selectedClass = classes.find(c => c.class_id === examsClassFilter);
        
        // Match if class_id equals filter (direct assignment)
        if (e.class_id === examsClassFilter) return true;
        
        // Match if target_classes explicitly includes the filter class
        if (e.target_classes && e.target_classes.includes(examsClassFilter)) return true;
        
        // For "all classes" exams (no specific target), check creation dates
        // Only show if the class was created BEFORE or AT the same time as the exam
        if (!e.class_id && (!e.target_classes || e.target_classes.length === 0)) {
          // If class has no created_at, assume it's old and should be included
          if (!selectedClass?.created_at) return true;
          // If exam has no created_at, assume it's old and should be included
          if (!e.created_at) return true;
          
          // Compare dates - exam should be created AFTER or AT the same time as the class
          const examDate = new Date(e.created_at);
          const classDate = new Date(selectedClass.created_at);
          return examDate >= classDate;
        }
        
        // For exams with target_classes that include all current classes
        // Check if it was an "all classes" exam at creation time
        if (e.target_classes && e.target_classes.length > 0) {
          // If the selected class is in target_classes, it was explicitly selected
          if (e.target_classes.includes(examsClassFilter)) return true;
        }
        
        return false;
      });

  if (loading) {
    return (
      <div className="min-h-screen bg-cockpit-bg flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cockpit-bg">
      {/* Header */}
      <header className="border-b border-cockpit-border bg-cockpit-panel/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-3 sm:px-6 py-2 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-cyan-500 rounded-lg flex items-center justify-center">
                <GraduationCap className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div>
                <h1 className="font-chivo text-sm sm:text-lg text-cockpit-text-bright">ULM Training Centre</h1>
                <p className="text-[10px] sm:text-xs text-slate-500 font-mono hidden sm:block">Instrutor</p>
              </div>
            </div>
            
            <div className="flex items-center gap-1 sm:gap-2">
              {/* Navigation buttons */}
              <Button 
                variant="ghost"
                size="sm"
                onClick={() => navigate("/dashboard")}
                className="text-slate-400 hover:text-emerald-400 text-xs p-1.5 sm:p-2"
                title="Dashboard Aluno"
              >
                <LayoutDashboard className="w-4 h-4" />
              </Button>
              {(user?.role === 'admin' || user?.role === 'super_admin') && (
                <Button 
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/admin")}
                  className="text-slate-400 hover:text-red-400 text-xs p-1.5 sm:p-2"
                  title="Painel Admin"
                >
                  <Shield className="w-4 h-4" />
                </Button>
              )}
              <Button 
                variant="ghost"
                size="sm"
                onClick={() => navigate("/missions")}
                className="text-slate-400 hover:text-purple-400 text-xs p-1.5 sm:p-2 hidden sm:flex"
                title="Missões"
              >
                <Rocket className="w-4 h-4" />
              </Button>
              <Button 
                variant="ghost"
                size="sm"
                onClick={() => navigate("/badges")}
                className="text-slate-400 hover:text-yellow-400 text-xs p-1.5 sm:p-2 hidden sm:flex"
                title="Badges"
              >
                <Award className="w-4 h-4" />
              </Button>
              <SinoPessoal />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/reservas")}
                className="text-slate-400 hover:text-orange-300 text-xs p-1.5 sm:p-2"
                title="Reservas de voo"
                data-testid="instrutor-reservas"
              >
                <CalendarCheck className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/registar-voo")}
                className="text-slate-400 hover:text-sky-300 text-xs p-1.5 sm:p-2"
                title="Registar voo"
                data-testid="instrutor-registar-voo"
              >
                <PlaneTakeoff className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/meus-documentos")}
                className="text-slate-400 hover:text-sky-300 text-xs p-1.5 sm:p-2"
                title="Os meus documentos"
                data-testid="instrutor-meus-documentos"
              >
                <FileText className="w-4 h-4" />
              </Button>
              <div className="w-px h-4 sm:h-6 bg-slate-700 mx-1 sm:mx-2" />
              
              {/* User section - Avatar dropdown on mobile, full info on desktop */}
              <div className="flex items-center gap-2 sm:gap-3">
                {/* Desktop: show name and role */}
                <div className="text-right hidden sm:block">
                  <p className="text-sm text-cockpit-text-bright">{user?.name}</p>
                  <p className="text-xs text-cyan-400 font-mono">Instrutor</p>
                </div>
                
                {/* Avatar with dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="w-8 h-8 sm:w-9 sm:h-9 rounded-full overflow-hidden border-2 border-slate-600 hover:border-cyan-500 transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500/50">
                      {user?.picture ? (
                        <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-cyan-500 flex items-center justify-center text-white font-bold text-sm">
                          {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700 min-w-[200px]">
                    <div className="px-3 py-2 border-b border-slate-700">
                      <p className="text-sm text-cockpit-text-bright font-medium">{user?.name}</p>
                      <p className="text-xs text-cyan-400">Instrutor</p>
                    </div>
                    {/* Mobile navigation items */}
                    <div className="sm:hidden py-1">
                      <DropdownMenuItem onClick={() => navigate("/missions")} className="text-slate-300 hover:text-white cursor-pointer">
                        <Rocket className="w-4 h-4 mr-2 text-purple-400" />
                        Missões
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/badges")} className="text-slate-300 hover:text-white cursor-pointer">
                        <Award className="w-4 h-4 mr-2 text-yellow-400" />
                        Badges
                      </DropdownMenuItem>
                    </div>
                    <DropdownMenuItem onClick={() => navigate(helpPath("instrutor"))} className="text-slate-300 hover:text-white cursor-pointer" data-testid="menu-help">
                      <HelpBook className="w-4 h-4 mr-2 text-sky-400" />
                      Manual de ajuda
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleLogout} className="text-red-400 hover:text-red-300 cursor-pointer">
                      <LogOut className="w-4 h-4 mr-2" />
                      Terminar Sessão
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-cockpit-panel border border-cockpit-border flex flex-wrap h-auto gap-1 p-1 justify-start">
            <TabsTrigger value="overview" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400">
              Visão Geral
            </TabsTrigger>
            <TabsTrigger value="classes" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400">
              Turmas
            </TabsTrigger>
            <TabsTrigger value="exams" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400">
              Exames
            </TabsTrigger>
            <TabsTrigger value="students" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400">
              Alunos
            </TabsTrigger>
            <TabsTrigger value="errors" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400">
              <span className="hidden sm:inline">Erros Frequentes</span>
              <span className="sm:hidden">Erros</span>
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Stats Cards with Links */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card 
                className="instrument-panel cursor-pointer hover:border-cyan-500/50 transition-colors"
                onClick={() => setActiveTab("students")}
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-cyan-500/10 rounded-lg flex items-center justify-center text-cyan-400">
                      <Users className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="font-mono text-2xl text-cyan-400">{stats?.total_students || 0}</div>
                      <div className="text-xs text-slate-500 uppercase">Alunos</div>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-800">
                    <span className="text-xs text-cyan-400 flex items-center gap-1">
                      Ver todos os alunos <ChevronRight className="w-3 h-3" />
                    </span>
                  </div>
                </CardContent>
              </Card>
              
              <Card 
                className="instrument-panel cursor-pointer hover:border-orange-500/50 transition-colors"
                onClick={() => setActiveTab("classes")}
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-orange-500/10 rounded-lg flex items-center justify-center text-orange-400">
                      <BookOpen className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="font-mono text-2xl text-orange-400">{stats?.total_classes || 0}</div>
                      <div className="text-xs text-slate-500 uppercase">Turmas</div>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-800">
                    <span className="text-xs text-orange-400 flex items-center gap-1">
                      Gerir turmas <ChevronRight className="w-3 h-3" />
                    </span>
                  </div>
                </CardContent>
              </Card>
              
              <Card 
                className="instrument-panel cursor-pointer hover:border-emerald-500/50 transition-colors"
                onClick={() => setActiveTab("exams")}
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-emerald-500/10 rounded-lg flex items-center justify-center text-emerald-400">
                      <FileText className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="font-mono text-2xl text-emerald-400">{stats?.total_exams || 0}</div>
                      <div className="text-xs text-slate-500 uppercase">Exames</div>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-800">
                    <span className="text-xs text-emerald-400 flex items-center gap-1">
                      Gerir exames <ChevronRight className="w-3 h-3" />
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Classes Tab */}
          <TabsContent value="classes" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="font-chivo text-xl text-cockpit-text-bright">As Minhas Turmas</h2>
              <Dialog open={showNewClassDialog} onOpenChange={setShowNewClassDialog}>
                <DialogTrigger asChild>
                  <Button className="btn-cockpit" data-testid="new-class-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Nova Turma
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-cockpit-panel border-cockpit-border">
                  <DialogHeader>
                    <DialogTitle className="font-chivo">Criar Nova Turma</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <label className="text-xs uppercase tracking-wider text-slate-500">Nome</label>
                      <Input 
                        value={newClassName}
                        onChange={(e) => setNewClassName(e.target.value)}
                        className="input-cockpit mt-1"
                        placeholder="Ex: Turma Janeiro 2024"
                        data-testid="new-class-name"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wider text-slate-500">Descrição</label>
                      <Input 
                        value={newClassDesc}
                        onChange={(e) => setNewClassDesc(e.target.value)}
                        className="input-cockpit mt-1"
                        placeholder="Descrição opcional"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wider text-slate-500">Início do curso</label>
                      <Input
                        type="date"
                        value={newClassStart}
                        onChange={(e) => setNewClassStart(e.target.value)}
                        className="input-cockpit mt-1 w-48"
                        data-testid="new-class-start"
                      />
                      <p className="text-[11px] text-slate-500 mt-1">Vale para todos os alunos da turma. Pode ser definida depois, em Editar turma.</p>
                    </div>
                    <Button onClick={createClass} className="btn-cockpit w-full" data-testid="create-class-btn">
                      Criar Turma
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {classes.length > 0 ? (
                classes.map((cls) => (
                  <ClassCard key={cls.class_id} classData={cls} onUpdate={fetchClasses} />
                ))
              ) : (
                <Card className="instrument-panel col-span-full">
                  <CardContent className="p-8 text-center">
                    <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">Ainda não tem turmas criadas</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Exams Tab */}
          <TabsContent value="exams" className="space-y-6">
            <div className="flex justify-between items-start sm:items-center flex-col sm:flex-row gap-3 sm:gap-4">
              <h2 className="font-chivo text-xl text-cockpit-text-bright">Os Meus Exames</h2>
              <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
                {/* Filter by class */}
                <div className="flex items-center gap-2 flex-1 sm:flex-initial min-w-0">
                  <span className="text-xs text-slate-500 shrink-0">Filtrar:</span>
                  <Select value={examsClassFilter} onValueChange={setExamsClassFilter}>
                    <SelectTrigger className="input-cockpit w-full sm:w-40">
                      <SelectValue placeholder="Todas as turmas" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      <SelectItem value="all">Todas as turmas</SelectItem>
                      {classes.map((cls) => (
                        <SelectItem key={cls.class_id} value={cls.class_id}>
                          {cls.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Dialog open={showNewExamDialog} onOpenChange={setShowNewExamDialog}>
                  <DialogTrigger asChild>
                    <Button className="btn-cockpit shrink-0" data-testid="new-exam-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      <span className="hidden sm:inline">Novo Exame</span>
                      <span className="sm:hidden">Novo</span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-cockpit-panel border-cockpit-border w-[95vw] max-w-lg max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="font-chivo">Criar Novo Exame</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      {/* Exam Presets */}
                      <div>
                        <label className="text-xs uppercase tracking-wider text-slate-500">Tipo de Exame</label>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 mt-2">
                          <Button
                            variant="outline"
                            className="btn-secondary-cockpit h-auto py-3 px-2 flex flex-col items-center gap-1 text-center"
                            onClick={() => {
                              setNewExamDuration(90);
                              setNewExamQuestions(50);
                              setNewExamCadernos(["caderno_1"]);
                              setNewExamSections([]);
                            }}
                          >
                            <span className="font-bold text-sm">Caderno I</span>
                            <span className="text-[10px] text-slate-500 whitespace-nowrap">50 quest. · 90 min</span>
                          </Button>
                          <Button
                            variant="outline"
                            className="btn-secondary-cockpit h-auto py-3 px-2 flex flex-col items-center gap-1 text-center"
                            onClick={() => {
                              setNewExamDuration(90);
                              setNewExamQuestions(50);
                              setNewExamCadernos(["caderno_2"]);
                              setNewExamSections([]);
                            }}
                          >
                            <span className="font-bold text-sm">Caderno II</span>
                            <span className="text-[10px] text-slate-500 whitespace-nowrap">50 quest. · 90 min</span>
                          </Button>
                          <Button
                            variant="outline"
                            className="btn-secondary-cockpit h-auto py-3 px-2 flex flex-col items-center gap-1 text-center"
                            onClick={() => {
                              setNewExamDuration(30);
                              setNewExamQuestions(23);
                              setNewExamCadernos(["caderno_com"]);
                              setNewExamSections([]);
                            }}
                          >
                            <span className="font-bold text-sm">Comunicações</span>
                            <span className="text-[10px] text-slate-500 whitespace-nowrap">23 quest. · 30 min</span>
                          </Button>
                        </div>
                        <p className="text-xs text-slate-500 mt-2">
                          Clique num preset para preencher automaticamente ou configure manualmente abaixo.
                        </p>
                      </div>
                      
                      <div className="border-t border-slate-700 pt-4">
                        <div>
                          <label className="text-xs uppercase tracking-wider text-slate-500">Título</label>
                          <Input 
                            value={newExamTitle}
                            onChange={(e) => setNewExamTitle(e.target.value)}
                            className="input-cockpit mt-1"
                            placeholder="Ex: Exame Final Janeiro"
                            data-testid="new-exam-title"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs uppercase tracking-wider text-slate-500">Duração (min)</label>
                          <Input 
                            type="number"
                            value={newExamDuration}
                            onChange={(e) => setNewExamDuration(parseInt(e.target.value))}
                            className="input-cockpit mt-1"
                          />
                        </div>
                        <div>
                          <label className="text-xs uppercase tracking-wider text-slate-500">Nº Questões (50-75)</label>
                          <Input 
                            type="number"
                            value={newExamQuestions}
                            onChange={(e) => setNewExamQuestions(parseInt(e.target.value))}
                            className="input-cockpit mt-1"
                            min={23}
                            max={75}
                          />
                        </div>
                      </div>
                      
                      {/* Difficulty Level */}
                      <div>
                        <label className="text-xs uppercase tracking-wider text-slate-500">Nível de Dificuldade</label>
                        <Select 
                          value={newExamDifficulty || "none"} 
                          onValueChange={(val) => setNewExamDifficulty(val === "none" ? "" : val)}
                        >
                          <SelectTrigger className="input-cockpit mt-1">
                            <SelectValue placeholder="Selecione o nível" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-900 border-slate-700">
                            <SelectItem value="none">Aleatório (sem filtro)</SelectItem>
                            <SelectItem value="easy">
                              <span className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                                Fácil (60% fáceis, 30% médias, 10% difíceis)
                              </span>
                            </SelectItem>
                            <SelectItem value="medium">
                              <span className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-yellow-400"></span>
                                Médio (30% fáceis, 50% médias, 20% difíceis)
                              </span>
                            </SelectItem>
                            <SelectItem value="hard">
                              <span className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-red-400"></span>
                                Difícil (10% fáceis, 40% médias, 50% difíceis)
                              </span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-slate-500 mt-1">
                          Baseado na taxa de acerto histórica das perguntas
                        </p>
                      </div>
                      
                      {/* Preparation Exam toggle */}
                      <div className="border border-slate-700 rounded-lg p-3 bg-slate-900/30">
                        <label className="flex items-start gap-3 cursor-pointer" data-testid="counts-for-stats-label">
                          <input
                            type="checkbox"
                            checked={newExamCountsForStats}
                            onChange={(e) => setNewExamCountsForStats(e.target.checked)}
                            className="mt-1 w-4 h-4 rounded border-slate-600 bg-slate-800 text-orange-500 focus:ring-orange-500 cursor-pointer"
                            data-testid="counts-for-stats-checkbox"
                          />
                          <div className="flex-1">
                            <div className="text-sm text-slate-200 font-medium">
                              Conta para estatísticas e médias
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">
                              Desmarca para criar um <strong className="text-amber-400">exame de preparação</strong> que não afeta as médias, o ranking nem a classificação dos alunos.
                            </div>
                          </div>
                        </label>
                      </div>
                      
                      <div>
                        <label className="text-xs uppercase tracking-wider text-slate-500">Turma</label>
                        <Select 
                          value={newExamClass || "none"} 
                          onValueChange={(val) => {
                            const classId = val === "none" ? "" : val;
                            setNewExamClass(classId);
                            setNewExamStudentFilter("all");
                            setNewExamSelectedStudents([]);
                            if (classId) {
                              fetchClassStudents(classId);
                            } else {
                              setClassStudents([]);
                            }
                          }}
                        >
                          <SelectTrigger className="input-cockpit mt-1">
                            <SelectValue placeholder="Selecione uma turma" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-900 border-slate-700">
                            <SelectItem value="none">Todas as turmas</SelectItem>
                            {classes.map((cls) => (
                              <SelectItem key={cls.class_id} value={cls.class_id}>
                                {cls.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Student Filter - Only show when a class is selected */}
                      {newExamClass && (
                        <div className="border border-slate-700 rounded-lg p-3 bg-slate-900/30">
                          <label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">
                            Filtrar Alunos
                          </label>
                          
                          {/* Filter Type Selection */}
                          <div className="flex gap-2 mb-3">
                            <Button
                              type="button"
                              variant={newExamStudentFilter === "all" ? "default" : "outline"}
                              size="sm"
                              className={newExamStudentFilter === "all" ? "btn-cockpit" : "btn-secondary-cockpit"}
                              onClick={() => {
                                setNewExamStudentFilter("all");
                                setNewExamSelectedStudents([]);
                              }}
                            >
                              Todos ({classStudents.length})
                            </Button>
                            <Button
                              type="button"
                              variant={newExamStudentFilter === "select" ? "default" : "outline"}
                              size="sm"
                              className={newExamStudentFilter === "select" ? "btn-cockpit" : "btn-secondary-cockpit"}
                              onClick={() => {
                                setNewExamStudentFilter("select");
                                setNewExamSelectedStudents([]);
                              }}
                            >
                              Selecionar
                            </Button>
                            <Button
                              type="button"
                              variant={newExamStudentFilter === "exclude" ? "default" : "outline"}
                              size="sm"
                              className={newExamStudentFilter === "exclude" ? "btn-cockpit" : "btn-secondary-cockpit"}
                              onClick={() => {
                                setNewExamStudentFilter("exclude");
                                setNewExamSelectedStudents([]);
                              }}
                            >
                              Excluir
                            </Button>
                          </div>
                          
                          {/* Student List - Only show if not "all" */}
                          {newExamStudentFilter !== "all" && (
                            <>
                              <p className="text-xs text-slate-400 mb-2">
                                {newExamStudentFilter === "select" 
                                  ? "Selecione os alunos que irão realizar o exame:"
                                  : "Selecione os alunos a excluir do exame:"}
                              </p>
                              
                              {loadingClassStudents ? (
                                <div className="flex justify-center py-4">
                                  <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                                </div>
                              ) : classStudents.length > 0 ? (
                                <div className="max-h-40 overflow-y-auto p-2 bg-slate-800/50 rounded border border-slate-700 space-y-1">
                                  {/* Select/Deselect All */}
                                  <div className="flex gap-2 mb-2 pb-2 border-b border-slate-700">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="text-xs text-cyan-400 hover:text-cyan-300 p-1 h-auto"
                                      onClick={() => setNewExamSelectedStudents(classStudents.map(s => s.user_id))}
                                    >
                                      Selecionar todos
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="text-xs text-slate-400 hover:text-slate-300 p-1 h-auto"
                                      onClick={() => setNewExamSelectedStudents([])}
                                    >
                                      Limpar
                                    </Button>
                                  </div>
                                  
                                  {classStudents.map((student) => (
                                    <label 
                                      key={student.user_id} 
                                      className="flex items-center gap-2 cursor-pointer hover:bg-slate-700/50 p-1.5 rounded"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={newExamSelectedStudents.includes(student.user_id)}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setNewExamSelectedStudents([...newExamSelectedStudents, student.user_id]);
                                          } else {
                                            setNewExamSelectedStudents(newExamSelectedStudents.filter(id => id !== student.user_id));
                                          }
                                        }}
                                        className="rounded border-slate-600 bg-slate-800 text-orange-500 focus:ring-orange-500"
                                      />
                                      <div className="flex-1 min-w-0">
                                        <span className="text-sm text-cockpit-text-bright block truncate">{student.name}</span>
                                        <span className="text-xs text-slate-500 truncate">{student.email}</span>
                                      </div>
                                    </label>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-xs text-slate-500 text-center py-2">Nenhum aluno nesta turma</p>
                              )}
                              
                              {/* Summary */}
                              {newExamSelectedStudents.length > 0 && (
                                <p className="text-xs text-orange-400 mt-2">
                                  {newExamStudentFilter === "select" 
                                    ? `${newExamSelectedStudents.length} aluno(s) selecionado(s)`
                                    : `${classStudents.length - newExamSelectedStudents.length} aluno(s) irão realizar o exame`}
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      )}
                      
                      {/* Cadernos Selection */}
                      <div>
                        <label className="text-xs uppercase tracking-wider text-slate-500">
                          Cadernos <span className="text-slate-600">(opcional - deixe vazio para todos)</span>
                        </label>
                        <div className="mt-2 grid grid-cols-1 gap-2 max-h-32 overflow-y-auto p-2 bg-slate-900/50 rounded-lg border border-slate-800">
                          {[...cadernos].sort((a, b) => a.caderno_name.localeCompare(b.caderno_name, 'pt')).map((caderno) => (
                            <label key={caderno.caderno_id} className="flex items-center gap-2 cursor-pointer hover:bg-slate-800/50 p-1 rounded">
                              <input
                                type="checkbox"
                                checked={newExamCadernos.includes(caderno.caderno_id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setNewExamCadernos([...newExamCadernos, caderno.caderno_id]);
                                  } else {
                                    setNewExamCadernos(newExamCadernos.filter(c => c !== caderno.caderno_id));
                                  }
                                }}
                                className="rounded border-slate-600 bg-slate-800 text-orange-500 focus:ring-orange-500"
                              />
                              <span className="text-sm text-cockpit-text-bright">{caderno.caderno_name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      
                      {/* Sections Selection */}
                      <div>
                        <label className="text-xs uppercase tracking-wider text-slate-500">
                          Disciplinas <span className="text-slate-600">(opcional - deixe vazio para todas)</span>
                        </label>
                        <div className="mt-2 grid grid-cols-1 gap-2 max-h-40 overflow-y-auto p-2 bg-slate-900/50 rounded-lg border border-slate-800">
                          {[...sections].sort((a, b) => a.section_name.localeCompare(b.section_name, 'pt')).map((section) => (
                            <label key={section.section_id} className="flex items-center gap-2 cursor-pointer hover:bg-slate-800/50 p-1 rounded">
                              <input
                                type="checkbox"
                                checked={newExamSections.includes(section.section_id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setNewExamSections([...newExamSections, section.section_id]);
                                  } else {
                                    setNewExamSections(newExamSections.filter(s => s !== section.section_id));
                                  }
                                }}
                                className="rounded border-slate-600 bg-slate-800 text-orange-500 focus:ring-orange-500"
                              />
                              <span className="text-sm text-cockpit-text-bright">{section.section_name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      
                      <Button onClick={createExam} className="btn-cockpit w-full" data-testid="create-exam-btn">
                        Criar Exame
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {(() => {
              const regular = filteredExams.filter((e) => e.counts_for_stats !== false);
              const preparation = filteredExams.filter((e) => e.counts_for_stats === false);
              const renderExam = (exam) => (
                <ExamCard
                  key={exam.exam_id}
                  exam={exam}
                  onDelete={fetchData}
                  onStart={startExam}
                  onStop={stopExam}
                  classes={classes}
                  sections={sections}
                  cadernos={cadernos}
                />
              );
              if (filteredExams.length === 0) {
                return (
                  <Card className="instrument-panel">
                    <CardContent className="p-8 text-center">
                      <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                      <p className="text-slate-400">Ainda não tem exames criados</p>
                    </CardContent>
                  </Card>
                );
              }
              return (
                <div className="space-y-4">
                  {/* Exames (contam para estatísticas) ─────────────────── */}
                  <Card className="instrument-panel">
                    <CardHeader className="py-3">
                      <button
                        type="button"
                        onClick={() => setShowRegularExams((v) => !v)}
                        className="w-full flex items-center justify-between gap-2"
                        data-testid="toggle-regular-exams"
                      >
                        <CardTitle className="font-chivo text-base flex items-center gap-2">
                          <FileText className="w-4 h-4 text-orange-400" />
                          Exames
                          <span className="text-xs font-normal text-slate-500">({regular.length})</span>
                        </CardTitle>
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          {showRegularExams ? "Ocultar" : "Mostrar"}
                          <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showRegularExams ? "rotate-90" : ""}`} />
                        </span>
                      </button>
                    </CardHeader>
                    {showRegularExams && (
                      <CardContent>
                        {regular.length > 0 ? (
                          <div className="space-y-4">{regular.map(renderExam)}</div>
                        ) : (
                          <p className="text-sm text-slate-500 italic">Sem exames nesta categoria.</p>
                        )}
                      </CardContent>
                    )}
                  </Card>

                  {/* Exames de Preparação (não contam para estatísticas) ─ */}
                  <Card className="instrument-panel">
                    <CardHeader className="py-3">
                      <button
                        type="button"
                        onClick={() => setShowPreparationExams((v) => !v)}
                        className="w-full flex items-center justify-between gap-2"
                        data-testid="toggle-preparation-exams"
                      >
                        <CardTitle className="font-chivo text-base flex items-center gap-2">
                          <Target className="w-4 h-4 text-amber-400" />
                          Preparação
                          <span className="text-xs font-normal text-slate-500">({preparation.length})</span>
                        </CardTitle>
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          {showPreparationExams ? "Ocultar" : "Mostrar"}
                          <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showPreparationExams ? "rotate-90" : ""}`} />
                        </span>
                      </button>
                    </CardHeader>
                    {showPreparationExams && (
                      <CardContent>
                        {preparation.length > 0 ? (
                          <div className="space-y-4">{preparation.map(renderExam)}</div>
                        ) : (
                          <p className="text-sm text-slate-500 italic">Sem exames de preparação.</p>
                        )}
                      </CardContent>
                    )}
                  </Card>
                </div>
              );
            })()}
          </TabsContent>

          {/* Students Tab - Detailed Student Statistics */}
          <TabsContent value="students" className="space-y-6">
            <Card className="instrument-panel">
              <CardHeader>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <CardTitle className="font-chivo text-lg flex items-center gap-2">
                      <Users className="w-5 h-5 text-cyan-400" />
                      Progresso dos Alunos
                    </CardTitle>
                    <p className="text-xs text-slate-500 mt-1">Evolução detalhada por disciplina</p>
                  </div>
                  <div className="w-full sm:w-48">
                    <Select value={studentsClassFilter} onValueChange={setStudentsClassFilter}>
                      <SelectTrigger className="input-cockpit">
                        <SelectValue placeholder="Todas as turmas" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700">
                        <SelectItem value="all">Todas as turmas</SelectItem>
                        {[...classes].sort((a, b) => a.name.localeCompare(b.name, 'pt')).map((c) => (
                          <SelectItem key={c.class_id} value={c.class_id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {!loadingStudents && <AnacPanorama students={studentsStats} />}
                {loadingStudents ? (
                  <div className="flex justify-center py-8">
                    <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : studentsStats.length > 0 ? (
                  <div className="space-y-3">
                    {studentsStats.map((student, idx) => (
                      <div key={student.user_id} className="border border-slate-800 rounded-lg overflow-hidden">
                        {/* Student Header - Clickable */}
                        <div 
                          className="p-4 bg-slate-900/50 cursor-pointer hover:bg-slate-800/50 transition-colors"
                          onClick={() => setExpandedStudent(expandedStudent === student.user_id ? null : student.user_id)}
                        >
                          <div className="flex items-start sm:items-center gap-2 sm:gap-4">
                            <div className="w-6 sm:w-8 shrink-0 text-center font-mono text-[11px] sm:text-sm text-slate-500 pt-0.5 sm:pt-0">
                              #{idx + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2">
                                <div className="flex items-center gap-1.5">
                                  <span className="font-medium text-cockpit-text-bright truncate text-sm sm:text-base">
                                    {student.name}
                                  </span>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleEditStudentName(student.user_id, student.name); }}
                                    className="p-0.5 hover:bg-slate-700 rounded text-slate-500 hover:text-orange-400 transition-colors"
                                    title="Editar nome"
                                  >
                                    <Edit2 className="w-3 h-3" />
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setAnacStudent(student); }}
                                    className="p-0.5 hover:bg-slate-700 rounded text-slate-500 hover:text-sky-300 transition-colors"
                                    title="Teóricas ANAC"
                                    data-testid="anac-open-btn"
                                  >
                                    <BookOpen className="w-3 h-3" />
                                  </button>
                                </div>
                                <span className="text-[10px] sm:text-xs text-slate-400 truncate">
                                  {student.email}
                                </span>
                              </div>
                              {/* No telemóvel os treinos e os exames vão para uma linha própria, para dar largura ao nome */}
                              <div className="sm:hidden mt-1 text-[11px] text-slate-400">
                                <span className="text-cockpit-text-bright">{student.total_sessions} treinos</span>
                                {" · "}
                                <span className={student.exams_completed > 0 ? "text-emerald-400" : "text-slate-400"}>
                                  {student.exams_completed}/{student.total_exams_available || student.exams_completed} exames
                                </span>
                              </div>
                              {/* Domínio por Caderno - stack on mobile */}
                              <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-1 sm:gap-3 text-[10px] sm:text-xs mt-1">
                                <span className="text-slate-400 font-medium">Treinos:</span>{student.caderno_progress?.map((cp) => (
                                  <span key={cp.caderno_id} className="flex items-center gap-1">
                                    <span className="text-slate-500">{cp.caderno_name}:</span>
                                    <span className={`font-mono ${
                                      cp.accuracy >= 80 ? 'text-emerald-400' :
                                      cp.accuracy >= 60 ? 'text-yellow-400' :
                                      cp.accuracy >= 40 ? 'text-orange-400' :
                                      'text-red-400'
                                    }`}>{cp.accuracy || 0}%</span>
                                    <span className="text-slate-600">({cp.correct_answers}/{cp.total_questions})</span>
                                  </span>
                                ))}
                              </div>
                              {/* Últimos treinos: a nota de cada um (mais recente primeiro) */}
                              {student.ultimos_treinos?.length > 0 && (
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] sm:text-xs mt-1" data-testid={`ultimos-treinos-${student.user_id}`}>
                                  <span className="text-slate-500">Últimos treinos:</span>
                                  {student.ultimos_treinos.map((t, i) => (
                                    <span key={i} title={t.completed_at ? new Date(t.completed_at).toLocaleString('pt-PT') : ''}>
                                      <span className={`font-mono ${t.score == null ? 'text-slate-400' : t.score >= 75 ? 'text-emerald-400' : 'text-red-400'}`}>
                                        {t.score == null ? '—' : `${t.score}%`}
                                      </span>
                                      <span className="text-slate-600"> ({t.correct}/{t.total}{t.completed_at ? `, ${new Date(t.completed_at).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' })}` : ''})</span>
                                    </span>
                                  ))}
                                </div>
                              )}
                              {/* Teóricas ANAC: um cartão por caderno (estado, nota e tentativas) e, por baixo, sittings e próximo exame */}
                              {student.anac && (
                                <div className="mt-2 space-y-1.5" data-testid={`anac-row-${student.user_id}`}>
                                  <div className="text-[10px] text-slate-400 font-medium">Exames oficiais ANAC</div>
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-1.5">
                                    {Object.values(student.anac.exams).map((e) => (
                                      <div key={e.label} className={`min-w-0 break-words rounded border px-2 py-1 ${ANAC_STATUS_CLASS[e.status]}`}>
                                        <div className="text-xs font-semibold text-slate-100 break-words">{e.label}</div>
                                        <div className="text-xs break-words">{ANAC_STATUS_LABEL[e.status]}</div>
                                        <div className="text-[11px] text-slate-300 break-words">
                                          Nota {e.score != null ? `${e.score}%` : "—"} · Tentativas {e.attempts_used}/{e.attempts_max}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  <div className="flex flex-wrap items-center gap-1.5 text-[10px] sm:text-xs">
                                    <span className="text-slate-400">ANAC · Sittings {student.anac.seats_used}/{student.anac.seats_max}</span>
                                    {student.anac.next_exam ? (
                                      <span className={`px-1.5 py-0.5 rounded border ${nextExamClass(student.anac.next_exam.days_left)}`}>
                                        Próximo: {fmtDayIso(student.anac.next_exam.date)} · {student.anac.next_exam.label} · {student.anac.next_exam.days_left === 0 ? "hoje" : `faltam ${student.anac.next_exam.days_left} dias`}
                                      </span>
                                    ) : (
                                      <span className="px-1.5 py-0.5 rounded border border-slate-700 text-slate-500">Sem exame marcado</span>
                                    )}
                                    {student.anac.expired && <span className="px-1.5 py-0.5 rounded border border-red-500/50 bg-red-500/10 text-red-300">Prazo ultrapassado</span>}
                                    {student.anac.needs_new_course && <span className="px-1.5 py-0.5 rounded border border-red-500/50 bg-red-500/10 text-red-300">Novo curso</span>}
                                    {!student.anac.course_start && <span className="px-1.5 py-0.5 rounded border border-amber-500/50 bg-amber-500/10 text-amber-300">Sem início do curso</span>}
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="hidden sm:block text-right flex-shrink-0">
                              <div className="flex flex-col sm:flex-row items-end sm:items-center gap-0.5 sm:gap-3 text-[10px] sm:text-sm">
                                <span className="text-cockpit-text-bright" title="Sessões de treino concluídas">
                                  {student.total_sessions} treinos
                                </span>
                                <span className={`${student.exams_completed > 0 ? 'text-emerald-400' : 'text-slate-400'}`} title="Exames realizados / disponíveis">
                                  {student.exams_completed}/{student.total_exams_available || student.exams_completed} exames
                                </span>
                              </div>
                            </div>
                            <ChevronRight className={`w-4 h-4 sm:w-5 sm:h-5 text-slate-500 transition-transform flex-shrink-0 ${
                              expandedStudent === student.user_id ? 'rotate-90' : ''
                            }`} />
                          </div>
                        </div>
                        
                        {/* Expanded Details */}
                        {expandedStudent === student.user_id && (
                          <div className="p-4 border-t border-slate-800 bg-slate-900/30">
                            {/* Progress by Section */}
                            <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-3">
                              Progresso por Disciplina
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {student.section_progress?.filter(sp => sp.total_answered > 0 || sp.total_questions > 0).length > 0 ? (
                                student.section_progress
                                  .filter(sp => sp.total_questions > 0)
                                  .sort((a, b) => a.section_name.localeCompare(b.section_name, 'pt'))
                                  .map((sp) => (
                                <div key={sp.section_id} className="flex flex-col gap-1">
                                  <div className="flex justify-between items-start text-xs">
                                    <span className="text-slate-400 truncate flex-1 mr-2" title={sp.section_name}>
                                      {sp.section_name}
                                    </span>
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                      <span className={`font-mono ${
                                        sp.mastery >= 80 ? 'text-emerald-400' :
                                        sp.mastery >= 60 ? 'text-yellow-400' :
                                        sp.mastery >= 40 ? 'text-orange-400' :
                                        'text-red-400'
                                      }`}>
                                        {sp.mastery}%
                                      </span>
                                      <span className={`px-1 py-0.5 rounded text-[9px] font-medium ${
                                        sp.approval_status === 'approved' 
                                          ? 'bg-emerald-500/20 text-emerald-400' 
                                          : sp.approval_status === 'failed'
                                          ? 'bg-red-500/20 text-red-400'
                                          : 'bg-slate-600/30 text-slate-500'
                                      }`}>
                                        {sp.approval_status === 'approved' ? '✓' 
                                          : sp.approval_status === 'failed' ? '✗' 
                                          : '○'}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                                    <div 
                                      className={`h-full transition-all ${
                                        sp.mastery >= 80 ? 'bg-emerald-500' :
                                        sp.mastery >= 60 ? 'bg-yellow-500' :
                                        sp.mastery >= 40 ? 'bg-orange-500' :
                                        'bg-red-500'
                                      }`}
                                      style={{ width: `${sp.mastery}%` }}
                                    />
                                  </div>
                                  <div className="flex gap-3 text-[9px] font-mono text-slate-500">
                                    <span title="Cobertura">C: <span className="text-blue-400">{sp.coverage ?? 0}%</span></span>
                                    <span title="Domínio">D: <span className="text-orange-400">{sp.domain ?? 0}%</span></span>
                                    <span title="Estabilidade">E: <span className="text-emerald-400">{sp.stability ?? 0}%</span></span>
                                    <span className="text-slate-600">{sp.total_answered}/{sp.total_questions}</span>
                                    {sp.exams_total > 0 && (
                                      <span title={`Notas individuais (mais antigo → mais recente): ${(sp.exam_scores || []).map(s => `${s}%`).join(', ')}`}>
                                        <span className="text-cyan-400">Ex:</span>{' '}
                                        <span className={sp.avg_exam_accuracy >= 75 ? 'text-emerald-400' : 'text-red-400'}>
                                          {sp.avg_exam_accuracy ?? 0}%
                                        </span>
                                        {sp.exam_scores && sp.exam_scores.length > 0 && (
                                          <span className="text-slate-500">
                                            {' '}(
                                            {sp.exam_scores.map((score, idx) => (
                                              <span
                                                key={idx}
                                                className={score >= 75 ? 'text-emerald-400/80' : 'text-red-400/80'}
                                              >
                                                {score}%{idx < sp.exam_scores.length - 1 ? ', ' : ''}
                                              </span>
                                            ))}
                                            )
                                          </span>
                                        )}
                                        <ExamSparkline scores={sp.exam_scores} />
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))) : (
                                <p className="text-xs text-slate-500 col-span-2">Sem dados de treino</p>
                              )}
                            </div>
                            
                            {/* Compact Legend - wrap on mobile */}
                            <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-3 pt-2 border-t border-slate-800 text-[9px] sm:text-[10px] text-slate-500">
                              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded bg-emerald-500"></span>≥80%</span>
                              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded bg-yellow-500"></span>60-79%</span>
                              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded bg-orange-500"></span>40-59%</span>
                              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded bg-red-500"></span>&lt;40%</span>
                              <span className="hidden sm:inline ml-1">|</span>
                              <span><span className="text-blue-400">C</span>=Cobertura</span>
                              <span><span className="text-orange-400">D</span>=Domínio</span>
                              <span><span className="text-emerald-400">E</span>=Estabilidade</span>
                              <span><span className="text-cyan-400">Ex</span>=Média Exames (tendência)</span>
                              <span className="hidden sm:inline ml-1">|</span>
                              <span>✓ Aprov.</span>
                              <span>✗ Reprov.</span>
                              <span>○ S/exam</span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Nenhum aluno encontrado</p>
                    <p className="text-xs mt-1">Adicione alunos às suas turmas para ver estatísticas</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Errors Tab - Most Missed Questions */}
          <TabsContent value="errors" className="space-y-6">
            <Card className="instrument-panel">
              <CardHeader>
                <CardTitle className="font-chivo text-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-400" />
                  Questões Mais Erradas
                </CardTitle>
                <p className="text-xs text-slate-500">Questões onde os alunos têm mais dificuldade</p>
                
                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-4">
                  <div className="flex-1 min-w-0">
                    <label className="text-xs uppercase tracking-wider text-slate-500">Filtrar por Turma</label>
                    <Select value={statsClassFilter} onValueChange={setStatsClassFilter}>
                      <SelectTrigger className="input-cockpit mt-1">
                        <SelectValue placeholder="Todas as turmas" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700">
                        <SelectItem value="all">Todas as turmas</SelectItem>
                        {[...classes].sort((a, b) => a.name.localeCompare(b.name, 'pt')).map((c) => (
                          <SelectItem key={c.class_id} value={c.class_id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1 min-w-0">
                    <label className="text-xs uppercase tracking-wider text-slate-500">Filtrar por Disciplina</label>
                    <Select value={statsSectionFilter} onValueChange={setStatsSectionFilter}>
                      <SelectTrigger className="input-cockpit mt-1">
                        <SelectValue placeholder="Todas as disciplinas" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700">
                        <SelectItem value="all">Todas as disciplinas</SelectItem>
                        {[...sections].sort((a, b) => a.section_name.localeCompare(b.section_name, 'pt')).map((s) => (
                          <SelectItem key={s.section_id} value={s.section_id}>
                            {s.section_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {loadingErrors ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                  </div>
                ) : (filteredStats || stats)?.most_missed_questions?.length > 0 ? (
                  (filteredStats || stats).most_missed_questions.map((q, i) => (
                    <div key={i} className="p-4 bg-slate-900/50 rounded-lg border border-slate-800">
                      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-cockpit-text-bright break-words">{q.statement}</p>
                          {q.image && (
                            <img src={q.image} alt="Imagem da questão" className="mt-2 rounded-lg max-h-40 object-contain border border-slate-700" />
                          )}
                          <p className="text-xs text-slate-500 mt-1">{q.section_name}</p>
                        </div>
                        <div className="badge-cockpit text-red-400 shrink-0 whitespace-nowrap">
                          {q.miss_count} erros
                        </div>
                      </div>
                      
                      {/* Answer Distribution with Full Text */}
                      {q.answers && (
                        <div className="space-y-3 mt-3 pt-3 border-t border-slate-700">
                          <p className="text-xs text-slate-500 uppercase">
                            Distribuição de Respostas ({q.total_responses || 0} respostas)
                          </p>
                          {q.answers.map((ans, idx) => {
                            const ansText = ans?.text || ans;
                            const ansId = ans?.id || `ans_${idx}`;
                            // Try multiple key formats for distribution
                            const count = q.answer_distribution?.[idx.toString()] 
                              || q.answer_distribution?.[ansId] 
                              || q.answer_distribution?.[`ans_${idx}`] 
                              || 0;
                            const percentage = (q.total_responses || 0) > 0 ? (count / q.total_responses * 100) : 0;
                            const isCorrect = ansId === q.correct_answer || idx === q.correct_answer || `ans_${idx}` === q.correct_answer;
                            const hasResponses = count > 0;
                            
                            return (
                              <div key={idx} className={`p-3 rounded-lg border ${
                                isCorrect 
                                  ? 'bg-emerald-500/10 border-emerald-500/30' 
                                  : hasResponses 
                                    ? 'bg-red-500/10 border-red-500/30'
                                    : 'bg-slate-800/50 border-slate-700'
                              }`}>
                                <div className="flex items-start gap-3">
                                  <span className={`w-7 h-7 rounded flex items-center justify-center text-sm font-mono shrink-0 ${
                                    isCorrect ? 'bg-emerald-500 text-white' : hasResponses ? 'bg-red-500 text-white' : 'bg-slate-700 text-slate-300'
                                  }`}>
                                    {['A', 'B', 'C', 'D'][idx]}
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-sm ${
                                      isCorrect ? 'text-emerald-300' : hasResponses ? 'text-red-300' : 'text-slate-300'
                                    }`}>
                                      {ansText}
                                      {isCorrect && <span className="ml-2 text-emerald-400 font-medium">✓ Correta</span>}
                                      {!isCorrect && hasResponses && <span className="ml-2 text-red-400 font-medium">✗ Errada</span>}
                                    </p>
                                    <div className="mt-2 flex items-center gap-2">
                                      <div className="flex-1 h-2 bg-slate-700 rounded overflow-hidden">
                                        <div 
                                          className={`h-full transition-all ${
                                            isCorrect ? 'bg-emerald-500' : 'bg-red-500'
                                          }`}
                                          style={{ width: `${percentage}%` }}
                                        />
                                      </div>
                                      <span className={`text-xs font-mono w-24 text-right font-bold ${
                                        isCorrect ? 'text-emerald-400' : hasResponses ? 'text-red-400' : 'text-slate-500'
                                      }`}>
                                        {count} ({percentage.toFixed(0)}%)
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-center text-slate-500 py-4">
                    {statsClassFilter !== "all" || statsSectionFilter !== "all" 
                      ? "Sem dados para os filtros selecionados" 
                      : "Sem dados disponíveis"}
                  </p>
                )}
                
                {/* Pagination Controls */}
                {errorsPagination && errorsPagination.total_pages > 1 && (
                  <div className="flex items-center justify-between pt-4 border-t border-slate-700 mt-4">
                    <p className="text-sm text-slate-500">
                      Página {errorsPagination.page} de {errorsPagination.total_pages} ({errorsPagination.total_items} questões)
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setErrorsPage(p => Math.max(1, p - 1))}
                        disabled={!errorsPagination.has_prev || loadingErrors}
                        className="border-slate-700 text-slate-300 hover:bg-slate-800"
                      >
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Anterior
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setErrorsPage(p => p + 1)}
                        disabled={!errorsPagination.has_next || loadingErrors}
                        className="border-slate-700 text-slate-300 hover:bg-slate-800"
                      >
                        Seguinte
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        
        {/* Teóricas ANAC (o instrutor regista e corrige) */}
        <AnacTheoryDialog student={anacStudent} open={!!anacStudent} onClose={() => setAnacStudent(null)} />

        {/* Edit Student Name Dialog */}
        <Dialog open={showEditStudentNameDialog} onOpenChange={setShowEditStudentNameDialog}>
          <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="font-chivo text-cockpit-text-bright flex items-center gap-2">
                <Edit2 className="w-5 h-5 text-orange-400" />
                Editar Nome do Aluno
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="student-name-edit" className="text-slate-300">Nome</Label>
                <Input
                  id="student-name-edit"
                  value={editingStudentName}
                  onChange={(e) => setEditingStudentName(e.target.value)}
                  placeholder="Nome do aluno"
                  className="bg-slate-800 border-slate-700 text-white"
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveStudentName()}
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowEditStudentNameDialog(false)}
                className="border-slate-700 text-slate-300"
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleSaveStudentName}
                disabled={savingStudentName || !editingStudentName?.trim()}
                className="btn-cockpit"
              >
                {savingStudentName ? "A guardar..." : "Guardar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

const ClassCard = ({ classData, onUpdate }) => {
  const [students, setStudents] = useState([]);
  const [availableStudents, setAvailableStudents] = useState([]);
  const [showStudents, setShowStudents] = useState(false);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [newStudentEmail, setNewStudentEmail] = useState("");
  const [adding, setAdding] = useState(false);
  
  // Section management state
  const [showSections, setShowSections] = useState(false);
  const [sectionsData, setSectionsData] = useState([]);
  const [loadingSections, setLoadingSections] = useState(false);

  // Edit student name state
  const [showEditNameDialog, setShowEditNameDialog] = useState(false);
  const [editingStudentId, setEditingStudentId] = useState(null);
  const [editingStudentName, setEditingStudentName] = useState("");
  const [savingName, setSavingName] = useState(false);

  // Editar a turma: nome, descrição e início do curso (a data vale para todos os alunos)
  const [showEditClass, setShowEditClass] = useState(false);
  const [editClassName, setEditClassName] = useState("");
  const [editClassDesc, setEditClassDesc] = useState("");
  const [editClassStart, setEditClassStart] = useState("");
  const [savingClass, setSavingClass] = useState(false);

  const openEditClass = () => {
    setEditClassName(classData.name || "");
    setEditClassDesc(classData.description || "");
    setEditClassStart(classData.course_start_date || "");
    setShowEditClass(true);
  };

  const saveClass = async () => {
    if (!editClassName.trim()) {
      toast.error("Nome da turma é obrigatório");
      return;
    }
    setSavingClass(true);
    try {
      await axios.put(`${API}/instructor/classes/${classData.class_id}`, {
        name: editClassName,
        description: editClassDesc,
        course_start_date: editClassStart,   // vazio: remove a data
      }, { withCredentials: true });
      toast.success("Turma atualizada");
      setShowEditClass(false);
      onUpdate?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a guardar a turma");
    } finally {
      setSavingClass(false);
    }
  };

  const handleEditStudentName = (studentId, currentName) => {
    setEditingStudentId(studentId);
    setEditingStudentName(currentName || "");
    setShowEditNameDialog(true);
  };

  const handleSaveStudentName = async () => {
    if (!editingStudentName.trim() || editingStudentName.trim().length < 2) {
      toast.error("O nome deve ter pelo menos 2 caracteres");
      return;
    }
    
    setSavingName(true);
    try {
      await axios.put(
        `${API}/instructor/students/${editingStudentId}/name`, 
        { name: editingStudentName.trim() }, 
        { withCredentials: true }
      );
      toast.success("Nome do aluno atualizado com sucesso");
      setShowEditNameDialog(false);
      fetchStudents();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar nome");
    } finally {
      setSavingName(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get(
        `${API}/instructor/classes/${classData.class_id}/students`,
        { withCredentials: true }
      );
      setStudents(response.data);
      setShowStudents(true);
    } catch (error) {
      toast.error("Erro ao carregar alunos");
    }
  };

  const fetchAvailableStudents = async () => {
    try {
      const response = await axios.get(
        `${API}/instructor/available-students`,
        { withCredentials: true }
      );
      setAvailableStudents(response.data);
    } catch (error) {
      console.error("Error fetching available students:", error);
    }
  };
  
  const fetchSections = async () => {
    setLoadingSections(true);
    try {
      const response = await axios.get(
        `${API}/instructor/classes/${classData.class_id}/unlocked-sections`,
        { withCredentials: true }
      );
      setSectionsData(response.data.sections || []);
      setShowSections(true);
    } catch (error) {
      toast.error("Erro ao carregar disciplinas");
    } finally {
      setLoadingSections(false);
    }
  };
  
  const toggleSection = async (sectionId, currentlyUnlocked) => {
    try {
      const endpoint = currentlyUnlocked 
        ? `${API}/instructor/classes/${classData.class_id}/lock-sections`
        : `${API}/instructor/classes/${classData.class_id}/unlock-sections`;
      
      await axios.post(endpoint, { section_ids: [sectionId] }, { withCredentials: true });
      
      // Update local state
      setSectionsData(prev => prev.map(s => 
        s.section_id === sectionId ? { ...s, is_unlocked: !currentlyUnlocked } : s
      ));
      
      toast.success(currentlyUnlocked ? "Disciplina bloqueada" : "Disciplina desbloqueada");
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Erro ao atualizar disciplina");
    }
  };
  
  const unlockAllSections = async () => {
    try {
      const lockedSections = sectionsData.filter(s => !s.is_unlocked).map(s => s.section_id);
      if (lockedSections.length === 0) {
        toast.info("Todas as disciplinas já estão desbloqueadas");
        return;
      }
      
      await axios.post(
        `${API}/instructor/classes/${classData.class_id}/unlock-sections`,
        { section_ids: lockedSections },
        { withCredentials: true }
      );
      
      setSectionsData(prev => prev.map(s => ({ ...s, is_unlocked: true })));
      toast.success("Todas as disciplinas desbloqueadas");
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Erro ao desbloquear disciplinas");
    }
  };
  
  const lockAllSections = async () => {
    try {
      const unlockedSections = sectionsData.filter(s => s.is_unlocked).map(s => s.section_id);
      if (unlockedSections.length === 0) {
        toast.info("Todas as disciplinas já estão bloqueadas");
        return;
      }
      
      await axios.post(
        `${API}/instructor/classes/${classData.class_id}/lock-sections`,
        { section_ids: unlockedSections },
        { withCredentials: true }
      );
      
      setSectionsData(prev => prev.map(s => ({ ...s, is_unlocked: false })));
      toast.success("Todas as disciplinas bloqueadas");
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Erro ao bloquear disciplinas");
    }
  };

  const addStudent = async () => {
    if (!newStudentEmail.trim()) {
      toast.error("Por favor introduza o email do aluno");
      return;
    }
    
    setAdding(true);
    try {
      await axios.post(
        `${API}/instructor/classes/${classData.class_id}/students?email=${encodeURIComponent(newStudentEmail)}`,
        {},
        { withCredentials: true }
      );
      toast.success("Aluno adicionado com sucesso");
      setNewStudentEmail("");
      setShowAddStudent(false);
      fetchStudents();
      fetchAvailableStudents(); // Refresh available students list
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao adicionar aluno");
    } finally {
      setAdding(false);
    }
  };

  const removeStudent = async (studentId) => {
    if (!confirm("Tem a certeza que deseja remover este aluno da turma?")) return;
    
    try {
      await axios.delete(
        `${API}/instructor/classes/${classData.class_id}/students/${studentId}`,
        { withCredentials: true }
      );
      toast.success("Aluno removido da turma");
      fetchStudents();
      fetchAvailableStudents(); // Refresh available students list
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Erro ao remover aluno");
    }
  };

  const deleteClass = async () => {
    if (!confirm(`Tem a certeza que deseja eliminar a turma "${classData.name}"?\n\nTodos os alunos associados ficarão sem turma.`)) return;
    
    try {
      await axios.delete(
        `${API}/instructor/classes/${classData.class_id}`,
        { withCredentials: true }
      );
      toast.success("Turma eliminada com sucesso");
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao eliminar turma");
    }
  };

  const handleOpenStudents = () => {
    fetchStudents();
    fetchAvailableStudents();
  };
  
  // Count unlocked sections
  const unlockedCount = classData.unlocked_sections?.length || 0;

  return (
    <Card className="instrument-panel">
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-start justify-between mb-4 gap-2">
          <div className="min-w-0 flex-1">
            <h3 className="font-chivo text-lg text-cockpit-text-bright truncate">{classData.name}</h3>
            {classData.description && (
              <p className="text-sm text-slate-400 mt-1 truncate">{classData.description}</p>
            )}
            {classData.course_start_date ? (
              <p className="text-xs text-slate-300 mt-1" data-testid="class-course-start">
                Início do curso: <span className="font-mono">{fmtDayIso(classData.course_start_date)}</span>
              </p>
            ) : (
              <p className="text-xs text-amber-300 mt-1" data-testid="class-course-start-missing">
                Falta a data de início do curso: sem ela não há prazo de 2 anos nem validade do cartão de aluno.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="badge-cockpit text-cyan-400 whitespace-nowrap">
              {classData.student_count} alunos
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={openEditClass}
              className="text-slate-400 hover:text-orange-300 flex-shrink-0"
              title="Editar turma"
              data-testid="class-edit-btn"
            >
              <Edit2 className="w-4 h-4" />
            </Button>
            <Dialog open={showEditClass} onOpenChange={setShowEditClass}>
              <DialogContent className="bg-cockpit-panel border-cockpit-border">
                <DialogHeader>
                  <DialogTitle className="font-chivo">Editar turma</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-slate-500">Nome</label>
                    <Input
                      value={editClassName}
                      onChange={(e) => setEditClassName(e.target.value)}
                      className="input-cockpit mt-1"
                      data-testid="edit-class-name"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-slate-500">Descrição</label>
                    <Input
                      value={editClassDesc}
                      onChange={(e) => setEditClassDesc(e.target.value)}
                      className="input-cockpit mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-slate-500">Início do curso</label>
                    <Input
                      type="date"
                      value={editClassStart}
                      onChange={(e) => setEditClassStart(e.target.value)}
                      className="input-cockpit mt-1 w-48"
                      data-testid="edit-class-start"
                    />
                    <p className="text-[11px] text-slate-500 mt-1">
                      Vale para todos os alunos da turma: com esta data calculam-se o prazo de 2 anos do curso e a validade do cartão de aluno. Para a apagar, deixa o campo vazio.
                    </p>
                  </div>
                  <Button onClick={saveClass} disabled={savingClass} className="btn-cockpit w-full" data-testid="save-class-btn">
                    {savingClass ? "A guardar..." : "Guardar"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <Button
              variant="ghost"
              size="icon"
              onClick={deleteClass}
              className="text-slate-400 hover:text-red-400 flex-shrink-0"
              title="Eliminar turma"
              data-testid={`delete-class-${classData.class_id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {/* Unlocked sections indicator */}
        <div className="mb-4 p-2 bg-slate-900/50 rounded border border-slate-800">
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-500">Disciplinas desbloqueadas:</span>
            <span className={`font-mono ${unlockedCount > 0 ? 'text-emerald-400' : 'text-yellow-400'}`}>
              {unlockedCount} / 10
            </span>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-2">
          {/* Manage Sections Button */}
          <Dialog open={showSections} onOpenChange={setShowSections}>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                onClick={fetchSections}
                className="w-full h-10 text-xs sm:text-sm bg-slate-800 hover:bg-slate-700 border-slate-600"
                data-testid={`manage-sections-${classData.class_id}`}
              >
                <BookOpen className="w-4 h-4 mr-1 sm:mr-2" />
                Disciplinas
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-cockpit-panel border-cockpit-border max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-chivo">Gerir Disciplinas - {classData.name}</DialogTitle>
              </DialogHeader>
              
              <p className="text-sm text-slate-400 mt-2">
                Selecione as disciplinas que os alunos desta turma podem treinar. 
                <span className="text-yellow-400"> Os exames não são afetados por esta configuração.</span>
              </p>
              
              {/* Quick Actions */}
              <div className="flex gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={unlockAllSections}
                  className="btn-secondary-cockpit"
                >
                  <Unlock className="w-3 h-3 mr-1" />
                  Desbloquear Todas
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={lockAllSections}
                  className="btn-secondary-cockpit"
                >
                  <Lock className="w-3 h-3 mr-1" />
                  Bloquear Todas
                </Button>
              </div>
              
              {/* Sections List */}
              <div className="mt-4 space-y-2 max-h-80 overflow-y-auto">
                {loadingSections ? (
                  <div className="text-center py-8 text-slate-500">A carregar...</div>
                ) : (
                  sectionsData.map((section) => (
                    <div 
                      key={section.section_id} 
                      className={`p-3 rounded-lg border transition-colors cursor-pointer ${
                        section.is_unlocked 
                          ? 'bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/20' 
                          : 'bg-slate-900/50 border-slate-800 hover:bg-slate-800/50'
                      }`}
                      onClick={() => toggleSection(section.section_id, section.is_unlocked)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {section.is_unlocked ? (
                            <Unlock className="w-4 h-4 text-emerald-400" />
                          ) : (
                            <Lock className="w-4 h-4 text-slate-500" />
                          )}
                          <span className={section.is_unlocked ? 'text-cockpit-text-bright' : 'text-slate-400'}>
                            {section.section_name}
                          </span>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          section.is_unlocked 
                            ? 'bg-emerald-500/20 text-emerald-400' 
                            : 'bg-slate-700 text-slate-500'
                        }`}>
                          {section.is_unlocked ? 'Desbloqueada' : 'Bloqueada'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </DialogContent>
          </Dialog>
        
          {/* Manage Students Button */}
          <Dialog open={showStudents} onOpenChange={setShowStudents}>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                onClick={handleOpenStudents}
                className="w-full h-10 text-xs sm:text-sm bg-slate-800 hover:bg-slate-700 border-slate-600"
                data-testid={`view-students-${classData.class_id}`}
              >
                <Eye className="w-4 h-4 mr-1 sm:mr-2" />
                Alunos
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-cockpit-panel border-cockpit-border max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-chivo">Alunos - {classData.name}</DialogTitle>
              </DialogHeader>
            
            {/* Add Student Section */}
            <div className="mt-4 p-4 bg-slate-900/50 rounded-lg border border-slate-800">
              <h4 className="text-sm font-medium text-cockpit-text-bright mb-3">Adicionar Aluno</h4>
              {!showAddStudent ? (
                <Button 
                  onClick={() => setShowAddStudent(true)}
                  className="btn-cockpit w-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Aluno
                </Button>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-slate-500">Email do Aluno</label>
                    <Input
                      type="email"
                      value={newStudentEmail}
                      onChange={(e) => setNewStudentEmail(e.target.value)}
                      className="input-cockpit mt-1"
                      placeholder="aluno@exemplo.com"
                    />
                  </div>
                  {availableStudents.length > 0 && (
                    <div>
                      <label className="text-xs uppercase tracking-wider text-slate-500">Ou selecione da lista</label>
                      <Select onValueChange={(email) => setNewStudentEmail(email)}>
                        <SelectTrigger className="input-cockpit mt-1">
                          <SelectValue placeholder="Utilizadores disponíveis" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-700">
                          {availableStudents.map((s) => (
                            <SelectItem key={s.user_id} value={s.email}>
                              {s.name} ({s.email})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      onClick={() => { setShowAddStudent(false); setNewStudentEmail(""); }}
                      className="btn-secondary-cockpit flex-1"
                    >
                      Cancelar
                    </Button>
                    <Button 
                      onClick={addStudent}
                      disabled={adding}
                      className="btn-cockpit flex-1"
                    >
                      {adding ? "A adicionar..." : "Adicionar"}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Students List */}
            <div className="mt-4 space-y-3 max-h-64 overflow-y-auto">
              {students.length > 0 ? (
                students.map((student) => (
                  <div key={student.user_id} className="p-4 bg-slate-900/50 rounded-lg border border-slate-800">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-cockpit-text-bright">{student.name}</p>
                          <button
                            onClick={() => handleEditStudentName(student.user_id, student.name)}
                            className="p-0.5 hover:bg-slate-700 rounded text-slate-500 hover:text-orange-400 transition-colors"
                            title="Editar nome"
                          >
                            <Edit2 className="w-3 h-3" />
                          </button>
                        </div>
                        <p className="text-xs text-slate-500 font-mono">{student.email}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className={`font-mono text-lg ${
                            student.accuracy >= 75 ? 'text-emerald-400' : 
                            student.accuracy >= 50 ? 'text-yellow-400' : 'text-red-400'
                          }`}>
                            {formatPercent(student.accuracy)}%
                          </div>
                          <div className="text-xs text-slate-500">
                            {student.correct_answers}/{student.total_answers}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeStudent(student.user_id)}
                          className="text-slate-400 hover:text-red-400"
                          title="Remover da turma"
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center text-slate-500 py-4">Nenhum aluno nesta turma</p>
              )}
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Edit Student Name Dialog */}
        <Dialog open={showEditNameDialog} onOpenChange={setShowEditNameDialog}>
          <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="font-chivo text-cockpit-text-bright flex items-center gap-2">
                <Edit2 className="w-5 h-5 text-orange-400" />
                Editar Nome do Aluno
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="class-student-name" className="text-slate-300">Nome</Label>
                <Input
                  id="class-student-name"
                  value={editingStudentName}
                  onChange={(e) => setEditingStudentName(e.target.value)}
                  placeholder="Nome do aluno"
                  className="bg-slate-800 border-slate-700 text-white"
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveStudentName()}
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowEditNameDialog(false)}
                className="border-slate-700 text-slate-300"
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleSaveStudentName}
                disabled={savingName || !editingStudentName?.trim()}
                className="btn-cockpit"
              >
                {savingName ? "A guardar..." : "Guardar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </CardContent>
    </Card>
  );
};

const ExamCard = ({ exam, onDelete, onStart, onStop, classes = [], sections = [], cadernos = [] }) => {
  const navigate = useNavigate();
  const [examData, setExamData] = useState(null);
  const [showManagement, setShowManagement] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentReview, setStudentReview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [monitorData, setMonitorData] = useState(null);
  const [showMonitor, setShowMonitor] = useState(false);
  const [openQuestionIdx, setOpenQuestionIdx] = useState(null);
  const [allUnlocked, setAllUnlocked] = useState(false);

  // Get class name
  const className = exam.class_id 
    ? classes.find(c => c.class_id === exam.class_id)?.name || "Turma desconhecida"
    : "Todas as turmas";

  // Get caderno names
  const cadernoNames = (exam.cadernos || [])
    .map(cid => cadernos.find(c => c.caderno_id === cid)?.caderno_name)
    .filter(Boolean);

  // Get section names
  const sectionNames = (exam.sections || [])
    .map(sid => sections.find(s => s.section_id === sid)?.section_name)
    .filter(Boolean);

  // Get exam status (draft, active, completed) - default to 'active' for legacy exams
  const examStatus = exam.status || (exam.is_active ? 'active' : 'completed');

  // Check if all reviews are unlocked
  const checkAllUnlocked = async () => {
    try {
      const response = await axios.get(
        `${API}/instructor/exams/${exam.exam_id}/results`,
        { withCredentials: true }
      );
      const students = response.data.students || [];
      const allAreUnlocked = students.length > 0 && students.every(s => s.result?.review_unlocked);
      setAllUnlocked(allAreUnlocked);
    } catch (error) {
      // Silently fail
    }
  };

  // Check unlock status when exam is completed
  useEffect(() => {
    if (examStatus === 'completed') {
      checkAllUnlocked();
    }
  }, [examStatus, exam.exam_id]);

  // Quick unlock all from card
  const quickUnlockAll = async () => {
    try {
      await axios.post(
        `${API}/instructor/exams/${exam.exam_id}/unlock-all`,
        {},
        { withCredentials: true }
      );
      setAllUnlocked(true);
      toast.success("Revisões desbloqueadas para todos os alunos");
    } catch (error) {
      toast.error("Erro ao desbloquear revisões");
    }
  };

  const fetchExamData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/instructor/exams/${exam.exam_id}/results`,
        { withCredentials: true }
      );
      setExamData(response.data);
      setShowManagement(true);
    } catch (error) {
      toast.error("Erro ao carregar dados do exame");
    } finally {
      setLoading(false);
    }
  };

  const fetchMonitorData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/instructor/exams/${exam.exam_id}/monitor`,
        { withCredentials: true }
      );
      setMonitorData(response.data);
      setShowMonitor(true);
    } catch (error) {
      toast.error("Erro ao carregar monitorização");
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh monitor every 3 seconds when open
  useEffect(() => {
    if (!showMonitor) return;
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(
          `${API}/instructor/exams/${exam.exam_id}/monitor`,
          { withCredentials: true }
        );
        setMonitorData(response.data);
      } catch (e) { /* silent */ }
    }, 3000);
    return () => clearInterval(interval);
  }, [showMonitor, exam.exam_id]);

  const fetchStudentReview = async (studentId) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/instructor/exams/${exam.exam_id}/student/${studentId}/review`,
        { withCredentials: true }
      );
      setStudentReview(response.data);
      setSelectedStudent(studentId);
    } catch (error) {
      toast.error("Erro ao carregar revisão do aluno");
    } finally {
      setLoading(false);
    }
  };

  const unlockReview = async (studentId) => {
    try {
      await axios.post(
        `${API}/instructor/exams/${exam.exam_id}/student/${studentId}/unlock-review`,
        {},
        { withCredentials: true }
      );
      toast.success("Revisão desbloqueada");
      fetchExamData(); // Refresh data
    } catch (error) {
      toast.error("Erro ao desbloquear revisão");
    }
  };

  const unlockAllReviews = async () => {
    try {
      await axios.post(
        `${API}/instructor/exams/${exam.exam_id}/unlock-all`,
        {},
        { withCredentials: true }
      );
      toast.success("Todas as revisões desbloqueadas");
      fetchExamData(); // Refresh data
    } catch (error) {
      toast.error("Erro ao desbloquear revisões");
    }
  };

  const deleteExam = async () => {
    if (!confirm("Tem a certeza que deseja eliminar este exame? Esta ação é irreversível e irá apagar todos os resultados e tentativas.")) return;
    
    try {
      await axios.delete(
        `${API}/instructor/exams/${exam.exam_id}`,
        { withCredentials: true }
      );
      toast.success("Exame eliminado com sucesso");
      setShowManagement(false);
      if (onDelete) onDelete();
    } catch (error) {
      toast.error("Erro ao eliminar exame");
    }
  };

  const deleteStudentResult = async (studentId, studentName) => {
    if (!confirm(`Tem a certeza que deseja eliminar o resultado do exame de ${studentName}? Esta ação é irreversível.`)) return;
    
    try {
      await axios.delete(
        `${API}/instructor/exams/${exam.exam_id}/student/${studentId}/result`,
        { withCredentials: true }
      );
      toast.success("Resultado do exame eliminado com sucesso");
      fetchExamData(); // Refresh data
    } catch (error) {
      console.error("Delete result error:", error.response?.data || error.message);
      toast.error(error.response?.data?.detail || "Erro ao eliminar resultado");
    }
  };

  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [newExamName, setNewExamName] = useState(exam.title || "");
  const [savingName, setSavingName] = useState(false);

  const renameExam = async () => {
    if (!newExamName.trim()) {
      toast.error("O nome não pode estar vazio");
      return;
    }
    
    setSavingName(true);
    try {
      await axios.put(
        `${API}/instructor/exams/${exam.exam_id}/name`,
        { name: newExamName.trim() },
        { withCredentials: true }
      );
      toast.success("Nome do exame atualizado");
      setShowRenameDialog(false);
      if (onDelete) onDelete(); // Refresh parent list
    } catch (error) {
      toast.error("Erro ao renomear exame");
    } finally {
      setSavingName(false);
    }
  };

  const toggleCountsForStats = async () => {
    const currentValue = exam.counts_for_stats !== false;
    const newValue = !currentValue;
    const confirmMsg = newValue
      ? "Tornar este exame contável para as estatísticas/médias dos alunos?"
      : "Marcar como exame de PREPARAÇÃO? Os resultados deixarão de contar para as médias e estatísticas dos alunos.";
    if (!confirm(confirmMsg)) return;
    try {
      await axios.put(
        `${API}/instructor/exams/${exam.exam_id}/counts-for-stats`,
        { counts_for_stats: newValue },
        { withCredentials: true }
      );
      toast.success(newValue ? "Exame agora conta para estatísticas" : "Exame marcado como Preparação");
      if (onDelete) onDelete(); // Refresh parent list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar configuração");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-4 h-4 text-emerald-400" />;
      case "in_progress":
        return <PlayCircle className="w-4 h-4 text-yellow-400" />;
      default:
        return <Clock className="w-4 h-4 text-slate-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "completed":
        return "Concluído";
      case "in_progress":
        return "Em Progresso";
      default:
        return "Não Iniciado";
    }
  };

  const getStatusCounts = () => {
    if (!examData?.students) return { completed: 0, in_progress: 0, not_started: 0 };
    return examData.students.reduce((acc, s) => {
      acc[s.status] = (acc[s.status] || 0) + 1;
      return acc;
    }, { completed: 0, in_progress: 0, not_started: 0 });
  };

  const statusCounts = getStatusCounts();

  // Format date
  const createdDate = exam.created_at 
    ? new Date(exam.created_at).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '';

  // Get stats
  const stats = exam.stats || { total_students: 0, completed: 0, passed: 0, pass_rate: 0 };

  return (
    <Card className="instrument-panel">
      <CardContent className="p-4 md:p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-chivo text-base md:text-lg text-cockpit-text-bright break-words">{exam.title}</h3>
              <span className="text-xs text-slate-500 font-mono">{createdDate}</span>
              {/* Status Badge */}
              {examStatus === 'draft' && (
                <span className="px-2 py-0.5 rounded text-xs font-mono bg-slate-500/20 text-slate-400">
                  Rascunho
                </span>
              )}
              {examStatus === 'active' && (
                <span className="px-2 py-0.5 rounded text-xs font-mono bg-emerald-500/20 text-emerald-400 animate-pulse">
                  Em Curso
                </span>
              )}
              {examStatus === 'completed' && (
                <span className="px-2 py-0.5 rounded text-xs font-mono bg-blue-500/20 text-blue-400">
                  Concluído
                </span>
              )}
              {exam.counts_for_stats === false && (
                <span
                  className="px-2 py-0.5 rounded text-xs font-mono bg-amber-500/20 text-amber-400 border border-amber-500/30"
                  title="Este exame não conta para as estatísticas nem para as médias dos alunos"
                  data-testid={`preparation-badge-${exam.exam_id}`}
                >
                  Preparação
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <p className="text-sm text-slate-400 font-mono">
                {exam.num_questions} questões · {exam.duration_minutes} min
              </p>
              <span className="text-xs px-2 py-0.5 rounded bg-cyan-500/10 text-cyan-400">
                {className}
              </span>
              {cadernoNames.length > 0 && (
                <span className="text-xs px-2 py-0.5 rounded bg-orange-500/10 text-orange-400">
                  {cadernoNames.join(", ")}
                </span>
              )}
              {sectionNames.length > 0 && (
                <span className="text-xs px-2 py-0.5 rounded bg-purple-500/10 text-purple-400" title={sectionNames.join(", ")}>
                  {sectionNames.length} disciplina{sectionNames.length > 1 ? "s" : ""}
                </span>
              )}
            </div>
            
            {/* Stats Row */}
            <div className="flex items-center gap-x-4 gap-y-2 mt-3 flex-wrap">
              <div className="flex items-center gap-1.5">
                <Users className="w-4 h-4 text-slate-500" />
                <span className={`text-sm font-mono ${stats.completed === stats.total_students && stats.total_students > 0 ? 'text-emerald-400' : 'text-slate-400'}`}>
                  {stats.completed}/{stats.total_students} concluídos
                </span>
              </div>
              {stats.in_progress > 0 && (
                <div className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm font-mono text-yellow-400">
                    {stats.in_progress} em progresso
                  </span>
                </div>
              )}
              {stats.completed > 0 && (
                <div className="flex items-center gap-1.5">
                  <TrendingUp className="w-4 h-4 text-slate-500" />
                  <span className={`text-sm font-mono ${stats.pass_rate >= 75 ? 'text-emerald-400' : stats.pass_rate >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                    {formatPercent(stats.pass_rate)}% aprovação
                  </span>
                </div>
              )}
              {/* Unlock status indicator */}
              {examStatus === 'completed' && stats.completed > 0 && (
                <div className="flex items-center gap-1.5">
                  {allUnlocked ? (
                    <>
                      <Unlock className="w-4 h-4 text-emerald-500" />
                      <span className="text-sm text-emerald-400">Desbloqueado</span>
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 text-orange-500" />
                      <span className="text-sm text-orange-400">Bloqueado</span>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
            {/* Start/Stop Exam Buttons */}
            {examStatus === 'draft' && (
              <Button 
                onClick={() => onStart && onStart(exam.exam_id)}
                className="btn-cockpit bg-emerald-600 hover:bg-emerald-500"
              >
                <PlayCircle className="w-4 h-4 mr-2" />
                Iniciar
              </Button>
            )}
            {examStatus === 'active' && (
              <>
                <Button 
                  variant="outline"
                  onClick={fetchMonitorData}
                  className="btn-secondary-cockpit"
                  disabled={loading}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Monitorizar
                </Button>
                <Button 
                  onClick={() => onStop && onStop(exam.exam_id)}
                  className="btn-cockpit bg-red-600 hover:bg-red-500"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Terminar
                </Button>
              </>
            )}
            {examStatus === 'completed' && (
              <>
                {!allUnlocked && (
                  <Button 
                    variant="outline"
                    onClick={quickUnlockAll}
                    className="btn-secondary-cockpit text-orange-400 border-orange-400/50 hover:bg-orange-500/10"
                    title="Desbloquear revisões para todos os alunos"
                  >
                    <Unlock className="w-4 h-4 mr-2" />
                    Desbloquear
                  </Button>
                )}
                <Button 
                  variant="outline"
                  onClick={() => navigate(`/exam/${exam.exam_id}/instructor-review`)}
                  className="btn-secondary-cockpit"
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Rever Exame
                </Button>
              </>
            )}
            
            <Dialog open={showManagement} onOpenChange={setShowManagement}>
              <DialogTrigger asChild>
                <Button 
                  variant="outline" 
                  onClick={fetchExamData}
                  className="btn-secondary-cockpit"
                  data-testid={`manage-exam-${exam.exam_id}`}
                  disabled={loading}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Gerir
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-cockpit-panel border-cockpit-border w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <div className="flex items-start justify-between flex-col sm:flex-row gap-3">
                    <div className="min-w-0">
                      <DialogTitle className="font-chivo text-lg md:text-xl break-words">{exam.title}</DialogTitle>
                      <p className="text-xs md:text-sm text-slate-400 mt-1">
                        {exam.num_questions} questões · {exam.duration_minutes} min · {className}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button 
                        variant="outline"
                        onClick={() => {
                          setNewExamName(exam.title || "");
                          setShowRenameDialog(true);
                        }}
                        className="btn-secondary-cockpit text-sm"
                        title="Renomear exame"
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Renomear
                      </Button>
                      <Button
                        variant="outline"
                        onClick={toggleCountsForStats}
                        className={
                          exam.counts_for_stats === false
                            ? "text-amber-400 border-amber-400/40 hover:bg-amber-500/10 text-sm"
                            : "btn-secondary-cockpit text-sm"
                        }
                        title={
                          exam.counts_for_stats === false
                            ? "Atualmente exame de preparação — clique para voltar a contar para estatísticas"
                            : "Marcar como exame de preparação (não conta para estatísticas)"
                        }
                        data-testid={`toggle-counts-${exam.exam_id}`}
                      >
                        {exam.counts_for_stats === false ? "Preparação" : "Conta p/ Stats"}
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={unlockAllReviews}
                        className="btn-secondary-cockpit text-sm"
                        title="Desbloquear todas as revisões"
                      >
                        <Unlock className="w-4 h-4 mr-1" />
                        Desbloquear Todos
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={deleteExam}
                        className="text-red-400 border-red-400/30 hover:bg-red-500/10 text-sm"
                        title="Eliminar exame"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </DialogHeader>

                {/* Status Summary */}
                <div className="grid grid-cols-3 gap-2 md:gap-4 mt-4">
                  <div className="p-2 md:p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/30 text-center">
                    <div className="font-mono text-xl md:text-2xl text-emerald-400">{statusCounts.completed}</div>
                    <div className="text-xs text-slate-400">Concluídos</div>
                  </div>
                  <div className="p-2 md:p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/30 text-center">
                    <div className="font-mono text-xl md:text-2xl text-yellow-400">{statusCounts.in_progress}</div>
                    <div className="text-xs text-slate-400">Em Progresso</div>
                  </div>
                  <div className="p-2 md:p-4 bg-slate-500/10 rounded-lg border border-slate-500/30 text-center">
                    <div className="font-mono text-xl md:text-2xl text-slate-400">{statusCounts.not_started}</div>
                    <div className="text-xs text-slate-400">Não Iniciados</div>
                  </div>
                </div>

                {/* Students List */}
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-cockpit-text-bright mb-3">Estado dos Alunos</h4>
                  
                  {selectedStudent && studentReview ? (
                    // Student Review Detail View
                    <div className="space-y-4">
                      <Button 
                        variant="ghost" 
                        onClick={() => { setSelectedStudent(null); setStudentReview(null); }}
                        className="text-slate-400 hover:text-white mb-2"
                      >
                        ← Voltar à lista
                      </Button>
                      
                      <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-800">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h5 className="font-medium text-cockpit-text-bright">{studentReview.student?.name}</h5>
                            <p className="text-xs text-slate-500 font-mono">{studentReview.student?.email}</p>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className={`font-mono text-xl ${
                                studentReview.result?.passed ? 'text-emerald-400' : 'text-red-400'
                              }`}>
                                {formatPercent(studentReview.result?.overall_accuracy)}%
                              </div>
                              <div className={`text-xs ${studentReview.result?.passed ? 'text-emerald-400' : 'text-red-400'}`}>
                                {studentReview.result?.passed ? 'Aprovado' : 'Reprovado'}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Section Results */}
                        {studentReview.result?.section_results && (
                          <div className="mb-4 grid grid-cols-2 md:grid-cols-3 gap-2">
                            {Object.entries(studentReview.result.section_results).map(([sectionId, data]) => (
                              <div key={sectionId} className={`p-2 rounded text-xs ${
                                data.passed ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
                              }`}>
                                <div className="font-medium truncate">{data.section_name}</div>
                                <div className="font-mono">{formatPercent(data.accuracy)}%</div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Questions Review */}
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                          {studentReview.questions?.map((q, idx) => (
                            <div key={q.question_id} className={`p-3 rounded-lg border ${
                              q.is_correct 
                                ? 'bg-emerald-500/5 border-emerald-500/30' 
                                : 'bg-red-500/5 border-red-500/30'
                            }`}>
                              <div className="flex items-start gap-3">
                                <span className={`w-6 h-6 rounded flex items-center justify-center text-xs font-mono shrink-0 ${
                                  q.is_correct ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
                                }`}>
                                  {idx + 1}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-cockpit-text-bright">{q.statement}</p>
                                  {q.image && (
                                    <img src={q.image} alt="Imagem da questão" className="mt-2 rounded-lg max-h-40 object-contain border border-slate-700" />
                                  )}
                                  <div className="mt-2 space-y-1">
                                    {q.answers?.map((ans, ansIdx) => {
                                      const ansText = ans?.text || ans;
                                      const ansId = ans?.id || `ans_${ansIdx}`;
                                      const isCorrectAnswer = ansId === q.correct_answer || ansIdx === q.correct_answer;
                                      const isUserAnswer = ansId === q.user_answer_id || ansIdx === q.user_answer;
                                      
                                      return (
                                        <div key={ansIdx} className={`text-xs p-1.5 rounded ${
                                          isCorrectAnswer
                                            ? 'bg-emerald-500/20 text-emerald-300' 
                                            : isUserAnswer && !q.is_correct
                                              ? 'bg-red-500/20 text-red-300'
                                              : 'text-slate-400'
                                        }`}>
                                          <span className="font-mono mr-2">{['A', 'B', 'C', 'D'][ansIdx]}.</span>
                                          {ansText}
                                          {isCorrectAnswer && <span className="ml-1">✓</span>}
                                          {isUserAnswer && !isCorrectAnswer && <span className="ml-1">(Selecionada)</span>}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Students List View
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {examData?.students?.length > 0 ? (
                        examData.students.map((student) => (
                          <div key={student.user_id} className="p-4 bg-slate-900/50 rounded-lg border border-slate-800">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                {getStatusIcon(student.status)}
                                <div>
                                  <p className="text-cockpit-text-bright">{student.name}</p>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    <p className="text-xs text-slate-500 font-mono">{student.email}</p>
                                    {student.class_name && (
                                      <span className="text-xs text-cyan-400">• {student.class_name}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-3">
                                {/* Status Badge */}
                                <div className={`px-2 py-1 rounded text-xs font-mono ${
                                  student.status === 'completed' 
                                    ? 'bg-emerald-500/10 text-emerald-400'
                                    : student.status === 'in_progress'
                                      ? 'bg-yellow-500/10 text-yellow-400'
                                      : 'bg-slate-500/10 text-slate-400'
                                }`}>
                                  {getStatusText(student.status)}
                                </div>
                                
                                {/* Score (if completed) */}
                                {student.result && (
                                  <div className={`font-mono text-lg ${
                                    student.result.passed ? 'text-emerald-400' : 'text-red-400'
                                  }`}>
                                    {formatPercent(student.result.overall_accuracy)}%
                                  </div>
                                )}
                                
                                {/* Actions */}
                                {student.status === 'completed' && (
                                  <div className="flex items-center gap-1">
                                    {/* Unlock Badge */}
                                    {student.result?.review_unlocked ? (
                                      <span className="text-xs text-emerald-400 flex items-center gap-1">
                                        <Unlock className="w-3 h-3" />
                                        Desbloqueado
                                      </span>
                                    ) : (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => unlockReview(student.user_id)}
                                        className="text-orange-400 hover:text-orange-300 text-xs"
                                        title="Desbloquear revisão"
                                      >
                                        <Lock className="w-3 h-3 mr-1" />
                                        Desbloquear
                                      </Button>
                                    )}
                                    
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => fetchStudentReview(student.user_id)}
                                      className="text-cyan-400 hover:text-cyan-300 text-xs"
                                      title="Ver respostas"
                                    >
                                      <Eye className="w-3 h-3 mr-1" />
                                      Ver
                                    </Button>
                                    
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => deleteStudentResult(student.user_id, student.name)}
                                      className="text-red-400 hover:text-red-300 text-xs"
                                      title="Eliminar resultado"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                )}
                                
                                {student.status === 'in_progress' && student.started_at && (
                                  <span className="text-xs text-slate-500">
                                    Iniciado: {new Date(student.started_at).toLocaleString('pt-PT')}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-center text-slate-500 py-8">
                          Nenhum aluno inscrito nas suas turmas
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
            
            {/* Rename Dialog */}
            <Dialog open={showRenameDialog} onOpenChange={setShowRenameDialog}>
              <DialogContent className="bg-cockpit-panel border-cockpit-border max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-chivo">Renomear Exame</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-slate-500">Nome do Exame</label>
                    <Input 
                      value={newExamName}
                      onChange={(e) => setNewExamName(e.target.value)}
                      className="input-cockpit mt-1"
                      placeholder="Ex: Exame Final Janeiro"
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="outline"
                      onClick={() => setShowRenameDialog(false)}
                      className="btn-secondary-cockpit"
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={renameExam}
                      disabled={savingName || !newExamName.trim()}
                      className="btn-cockpit"
                    >
                      {savingName ? "A guardar..." : "Guardar"}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            
            {/* Monitor Dialog */}
            <Dialog open={showMonitor} onOpenChange={(v) => { setShowMonitor(v); if (!v) setOpenQuestionIdx(null); }}>
              {/*<DialogContent className="bg-cockpit-panel border-cockpit-border max-w-5xl max-h-[90vh] overflow-y-auto"> */}
              <DialogContent className="bg-cockpit-panel border-cockpit-border w-screen max-w-screen h-[98vh] mx-auto my-2 p-3 md:p-4 overflow-y-auto">
                    <DialogHeader>
                  <DialogTitle className="font-chivo text-xl flex items-center gap-3">
                    <Eye className="w-6 h-6 text-cyan-400" />
                    Monitorização - {exam.title}
                    {monitorData?.time_remaining_seconds !== null && monitorData?.time_remaining_seconds !== undefined && (
                      <span className="ml-auto text-lg font-mono text-orange-400">
                        {Math.floor(monitorData.time_remaining_seconds / 60)}:{(monitorData.time_remaining_seconds % 60).toString().padStart(2, '0')} restantes
                      </span>
                    )}
                  </DialogTitle>
                </DialogHeader>
                
                {monitorData && (
                  <div className="mt-4 space-y-6">
                    {/* Summary Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                      <Card className="instrument-panel">
                        <CardContent className="p-4 text-center">
                          <Users className="w-6 h-6 text-cyan-400 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-cockpit-text-bright">{monitorData.summary?.total_assigned || 0}</p>
                          <p className="text-xs text-slate-500">Atribuídos</p>
                        </CardContent>
                      </Card>
                      <Card className="instrument-panel">
                        <CardContent className="p-4 text-center">
                          <PlayCircle className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-yellow-400">{monitorData.summary?.in_progress || 0}</p>
                          <p className="text-xs text-slate-500">Em Progresso</p>
                        </CardContent>
                      </Card>
                      <Card className="instrument-panel">
                        <CardContent className="p-4 text-center">
                          <CheckCircle className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-emerald-400">{monitorData.summary?.completed || 0}</p>
                          <p className="text-xs text-slate-500">Concluídos</p>
                        </CardContent>
                      </Card>
                      <Card className="instrument-panel">
                        <CardContent className="p-4 text-center">
                          <Clock className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-slate-400">
                            {(monitorData.summary?.total_assigned || 0) - (monitorData.summary?.started || 0)}
                          </p>
                          <p className="text-xs text-slate-500">Não Iniciaram</p>
                        </CardContent>
                      </Card>
                    </div>
                    
                    {/* Real-Time Grid Panel */}
                    {monitorData.questions?.length > 0 && monitorData.students_progress?.filter(s => s.status !== 'not_started').length > 0 && (
                      <div className="relative">
                        <h3 className="font-chivo text-lg text-cockpit-text-bright mb-3">Painel em Tempo Real</h3>
                        <div className="overflow-auto max-h-[400px] border border-slate-700 rounded-lg">
                          <table className="w-full text-xs" data-testid="exam-realtime-grid">
                            <thead className="sticky top-0 z-10 bg-slate-900">
                              <tr>
                                <th className="p-2 text-left text-slate-400 font-mono border-b border-r border-slate-700 sticky left-0 bg-slate-900 min-w-[40px]">#</th>
                                {monitorData.students_progress?.filter(s => s.status !== 'not_started').map((student) => {
                                  // Calculate provisional score from answers vs correct answers
                                  let answeredCount = 0;
                                  let correctCount = 0;
                                  if (student.answers && monitorData.questions) {
                                    for (const q of monitorData.questions) {
                                      const sAnswer = student.answers[q.question_id];
                                      if (sAnswer !== undefined) {
                                        answeredCount++;
                                        if (sAnswer === q.correct_answer) correctCount++;
                                      }
                                    }
                                  }
                                  const provisionalScore = answeredCount > 0 ? (correctCount / answeredCount) * 100 : 0;
                                  const totalQuestions = monitorData.questions?.length || 0;
                                  
                                  return (
                                  <th key={student.student_id} className="p-2 text-center text-slate-400 border-b border-slate-700 min-w-[80px]">
                                    <span className="block truncate max-w-[80px]" title={student.name}>
                                      {student.name?.split(' ')[0]}
                                    </span>
                                    {student.status === 'completed' ? (
                                      <span className={`text-[10px] font-mono ${student.passed ? 'text-emerald-400' : 'text-red-400'}`}>
                                        {formatPercent(student.score)}%
                                      </span>
                                    ) : answeredCount > 0 ? (
                                      <span className={`text-[10px] font-mono ${provisionalScore >= 75 ? 'text-emerald-400/70' : provisionalScore >= 50 ? 'text-yellow-400/70' : 'text-red-400/70'}`}>
                                        {formatPercent(provisionalScore)}% <span className="text-slate-600">({answeredCount}/{totalQuestions})</span>
                                      </span>
                                    ) : (
                                      <span className="text-[10px] font-mono text-slate-600">—</span>
                                    )}
                                  </th>
                                  );
                                })}
                              </tr>
                            </thead>
                            <tbody>
                              {monitorData.questions?.map((q, qIdx) => (
                                <tr key={q.question_id} className="border-b border-slate-800 hover:bg-slate-800/30">
                                  <td className="p-2 font-mono text-slate-500 border-r border-slate-700 sticky left-0 bg-slate-900/95">
                                    <button 
                                      className="hover:text-cyan-400 cursor-pointer"
                                      title={q.statement}
                                      onClick={() => setOpenQuestionIdx(openQuestionIdx === qIdx ? null : qIdx)}
                                    >
                                      Q{qIdx + 1}
                                    </button>
                                  </td>
                                  {monitorData.students_progress?.filter(s => s.status !== 'not_started').map((student) => {
                                    const sAnswer = student.answers?.[q.question_id];
                                    const answered = sAnswer !== undefined;
                                    const correct = answered && sAnswer === q.correct_answer;
                                    return (
                                      <td
                                        key={student.student_id}
                                        className={`p-2 text-center ${
                                          !answered
                                            ? 'bg-slate-800/20'
                                            : correct
                                              ? 'bg-emerald-500/20'
                                              : 'bg-red-500/20'
                                        }`}
                                        title={answered ? (correct ? 'Correto' : 'Errado') : 'Não respondida'}
                                      >
                                        {answered ? (
                                          correct 
                                            ? <CheckCircle className="w-4 h-4 text-emerald-400 mx-auto" />
                                            : <XCircle className="w-4 h-4 text-red-400 mx-auto" />
                                        ) : (
                                          <span className="text-slate-600">—</span>
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {/* Question Detail Popup - fixed centered */}
                        {openQuestionIdx !== null && monitorData.questions?.[openQuestionIdx] && (() => {
                          const q = monitorData.questions[openQuestionIdx];
                          return (
                            <div 
                              className="fixed inset-0 z-[9999] flex items-center justify-center"
                              onMouseDown={() => setOpenQuestionIdx(null)}
                            >
                              <div 
                                className="bg-slate-800 border border-slate-500 rounded-lg p-5 shadow-2xl w-96 max-h-72 overflow-y-auto"
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                <p className="text-[11px] text-slate-500 font-mono mb-1">Q{openQuestionIdx + 1} · {q.section_name}</p>
                                <p className="text-cockpit-text-bright text-sm mb-3">{q.statement}</p>
                                {q.image && <img src={q.image} alt="" className="max-h-28 rounded mb-3 object-contain" />}
                                <div className="space-y-1.5">
                                  {q.answers?.map((ans, aIdx) => {
                                    const aId = ans?.id || `ans_${aIdx}`;
                                    const isRight = aId === q.correct_answer;
                                    return (
                                      <p key={aIdx} className={`text-xs ${isRight ? 'text-emerald-400 font-bold' : 'text-slate-400'}`}>
                                        {['A','B','C','D'][aIdx]}. {ans?.text || ans}
                                      </p>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                        {/* Legend */}
                        <div className="flex gap-4 mt-2 text-xs text-slate-400">
                          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500/30 inline-block"></span> Correto</span>
                          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500/30 inline-block"></span> Errado</span>
                          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-slate-700/30 inline-block border border-slate-600"></span> Não respondida</span>
                          <span className="ml-auto">Clique em Q{'{n}'} para ver detalhes da pergunta</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Student Progress Table */}
                    <div>
                      <h3 className="font-chivo text-lg text-cockpit-text-bright mb-3">Progresso dos Alunos</h3>
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {monitorData.students_progress?.map((student) => (
                          <details 
                            key={student.student_id}
                            className={`rounded-lg border ${
                              student.status === 'completed' 
                                ? student.passed ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-red-500/10 border-red-500/30'
                                : student.status === 'in_progress'
                                  ? 'bg-yellow-500/10 border-yellow-500/30'
                                  : 'bg-slate-800/50 border-slate-700'
                            }`}
                          >
                            <summary className="p-3 cursor-pointer hover:bg-slate-800/30">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-cockpit-text-bright font-medium">{student.name}</p>
                                  <p className="text-xs text-slate-500">{student.email}</p>
                                </div>
                                <div className="flex items-center gap-4">
                                  {student.status === 'in_progress' && (() => {
                                    let ac = 0, cc = 0;
                                    if (student.answers && monitorData.questions) {
                                      for (const q of monitorData.questions) {
                                        const sa = student.answers[q.question_id];
                                        if (sa !== undefined) { ac++; if (sa === q.correct_answer) cc++; }
                                      }
                                    }
                                    const ps = ac > 0 ? (cc / ac) * 100 : 0;
                                    return (
                                    <>
                                      <span className={`text-sm font-mono ${ps >= 75 ? 'text-emerald-400/70' : ps >= 50 ? 'text-yellow-400' : 'text-red-400/70'}`}>
                                        {formatPercent(ps)}%
                                      </span>
                                      <span className="text-sm font-mono text-yellow-400">
                                        {student.answers_count}/{student.total_questions} respostas
                                      </span>
                                    </>
                                    );
                                  })()}
                                  {student.status === 'completed' && (
                                    <>
                                      <span className={`text-sm font-mono ${student.passed ? 'text-emerald-400' : 'text-red-400'}`}>
                                        {formatPercent(student.score)}%
                                      </span>
                                      <span className={`px-2 py-0.5 rounded text-xs ${student.passed ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                                        {student.passed ? 'Aprovado' : 'Reprovado'}
                                      </span>
                                    </>
                                  )}
                                  {student.status === 'not_started' && (
                                    <span className="text-sm text-slate-500">Não iniciou</span>
                                  )}
                                </div>
                              </div>
                            </summary>
                            
                            {/* Student's Answers Detail - NEW */}
                            {(student.status === 'in_progress' || student.status === 'completed') && student.answers && Object.keys(student.answers).length > 0 && (
                              <div className="px-3 pb-3 border-t border-slate-700 mt-2 pt-2">
                                <p className="text-xs text-slate-400 mb-2">Respostas do aluno:</p>
                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                  {monitorData.questions?.map((q, idx) => {
                                    const studentAnswerId = student.answers[q.question_id];
                                    const correctAnswerId = q.correct_answer;
                                    const isAnswered = studentAnswerId !== undefined;
                                    const isCorrect = studentAnswerId === correctAnswerId;
                                    
                                    // Find the answer text
                                    const answerObj = q.answers?.find(a => (a?.id || a) === studentAnswerId);
                                    const answerText = answerObj?.text || answerObj || '—';
                                    const answerIndex = q.answers?.findIndex(a => (a?.id || a) === studentAnswerId);
                                    const answerLetter = answerIndex >= 0 ? ['A', 'B', 'C', 'D'][answerIndex] : '?';
                                    
                                    return (
                                      <div 
                                        key={q.question_id}
                                        className={`text-xs p-2 rounded ${
                                          !isAnswered 
                                            ? 'bg-slate-800/50 text-slate-500'
                                            : isCorrect
                                              ? 'bg-emerald-500/10 text-emerald-400'
                                              : 'bg-red-500/10 text-red-400'
                                        }`}
                                      >
                                        <span className="font-mono mr-2">Q{idx + 1}:</span>
                                        {isAnswered ? (
                                          <>
                                            <span className="font-mono">{answerLetter}.</span>
                                            <span className="ml-1 truncate">{answerText.substring(0, 50)}{answerText.length > 50 ? '...' : ''}</span>
                                            {isCorrect && <CheckCircle className="w-3 h-3 inline ml-1" />}
                                            {!isCorrect && <XCircle className="w-3 h-3 inline ml-1" />}
                                          </>
                                        ) : (
                                          <span>Não respondida</span>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </details>
                        ))}
                      </div>
                    </div>
                    
                    {/* Questions Overview (Collapsed by default) */}
                    <details className="group">
                      <summary className="font-chivo text-lg text-cockpit-text-bright cursor-pointer hover:text-cyan-400 flex items-center gap-2">
                        <ChevronRight className="w-5 h-5 group-open:rotate-90 transition-transform" />
                        Ver Perguntas e Respostas ({monitorData.questions?.length || 0})
                      </summary>
                      <div className="mt-4 space-y-4 max-h-96 overflow-y-auto">
                        {monitorData.questions?.map((q, idx) => {
                          const correctAnswerId = q.correct_answer;
                          return (
                            <Card key={q.question_id} className="instrument-panel">
                              <CardContent className="p-4">
                                <p className="text-xs text-slate-500 mb-1">Questão {idx + 1} · {q.section_name}</p>
                                <p className="text-cockpit-text-bright mb-3">{q.statement}</p>
                                {q.image && (
                                  <img src={q.image} alt="Imagem da questão" className="mb-3 rounded-lg max-h-48 object-contain border border-slate-700" />
                                )}
                                <div className="space-y-1">
                                  {q.answers?.map((ans, ansIdx) => {
                                    const ansText = ans?.text || ans;
                                    const ansId = ans?.id || `ans_${ansIdx}`;
                                    const isCorrect = ansId === correctAnswerId;
                                    return (
                                      <div 
                                        key={ansIdx}
                                        className={`text-sm p-2 rounded ${isCorrect ? 'bg-emerald-500/20 text-emerald-300' : 'text-slate-400'}`}
                                      >
                                        <span className="font-mono mr-2">{['A', 'B', 'C', 'D'][ansIdx]}.</span>
                                        {ansText}
                                        {isCorrect && <CheckCircle className="w-4 h-4 inline ml-2" />}
                                      </div>
                                    );
                                  })}
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    </details>
                    
                    <div className="flex justify-end gap-2">
                      <Button 
                        variant="outline"
                        onClick={fetchMonitorData}
                        className="btn-secondary-cockpit"
                      >
                        Atualizar
                      </Button>
                      <Button 
                        onClick={() => setShowMonitor(false)}
                        className="btn-cockpit"
                      >
                        Fechar
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700">
                <DropdownMenuItem 
                  onClick={deleteExam}
                  className="text-red-400 focus:text-red-400 focus:bg-red-500/10 cursor-pointer"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar Exame
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default InstructorDashboard;
